/*Bearer ${token}`
 * ConfirmationStatementTab — agent-grade CS01 wizard
 *
 * Every step is fully self-contained: all operations required to complete
 * that step are available in-view without navigating away.
 *
 * Steps:
 *  0 — Company Data   : CH sync, address, SIC codes, statement of capital
 *  1 — Officers & PSC : directors (add/edit/resign) + PSCs (add/edit/cease)
 *  2 — Risk Review    : clickable progress boxes + quick-fix navigation (v2)
 *  3 — Approve        : pre-approval summary + declaration
 *  4 — Receipt        : locked audit record
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ShieldIcon from "@mui/icons-material/Shield";
import LaunchIcon from "@mui/icons-material/Launch";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import {
  listConfirmationStatements,
  sendCsReview,
  getLatestCsReview,
  agentApproveReview,
  fileConfirmationStatement,
  pollConfirmationStatementStatus,
  deleteConfirmationStatement,
  clearPendingCsReviews,
  updateClientProfile,
  createDirector,
  updateDirector,
  resyncClientFromCH,
getDocumentDownloadUrl, getPublicDocumentUrl, regenerateDocumentPublicToken, revokeDocumentPublicToken,
} from "services/tenantUserAdminService";
import { useAuthz } from "context/AuthzContext";
import { useConfirm } from "context/ConfirmContext";
import { TC, FS, FW } from "../../tokens/text";

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = [
  { label: "Company details", icon: BusinessIcon },
  { label: "People & control", icon: PersonIcon },
  { label: "Client review", icon: SendIcon },
  { label: "Final review", icon: HowToRegIcon },
  { label: "File & receipt", icon: TaskAltIcon },
];

const ROLE_OPTIONS = [
  { value: "director",   label: "Director"   },
  { value: "secretary",  label: "Secretary"  },
  { value: "llp_member", label: "LLP Member" },
  { value: "other",      label: "Other"      },
];

const PSC_KINDS = [
  { value: "individual-person-with-significant-control",    label: "Individual" },
  { value: "corporate-entity-person-with-significant-control", label: "Corporate Entity" },
  { value: "legal-person-with-significant-control",         label: "Legal Person" },
];

const NATURES_OF_CONTROL = [
  { value: "ownership-of-shares-25-to-50-percent",   label: "25–50% shares" },
  { value: "ownership-of-shares-50-to-75-percent",   label: "50–75% shares" },
  { value: "ownership-of-shares-75-to-100-percent",  label: "75–100% shares" },
  { value: "voting-rights-25-to-50-percent",         label: "25–50% voting" },
  { value: "voting-rights-50-to-75-percent",         label: "50–75% voting" },
  { value: "voting-rights-75-to-100-percent",        label: "75–100% voting" },
  { value: "right-to-appoint-and-remove-directors",  label: "Appoint/remove directors" },
  { value: "significant-influence-or-control",       label: "Significant influence" },
];

// flag code → wizard step that can fix it

const CURRENCIES = ["GBP", "EUR", "USD", "CHF"];

const DARK_CARD       = { background: "#ffffff", borderRadius: "10px", border: "1px solid #d8e6f7" };
const DARK_CARD_INNER = { background: "#f4f8ff", borderRadius: "8px",  border: "1px solid #d8e6f7" };

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
};

const daysUntil = (d) => {
  if (!d) return null;
  const t = new Date(d);
  return isNaN(t) ? null : Math.ceil((t - Date.now()) / 86400000);
};

const defaultMadeUpTo = (last) => {
  if (last) { const d = new Date(last); if (!isNaN(d)) { d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); } }
  return new Date().toISOString().slice(0, 10);
};


// ─── Shared UI ────────────────────────────────────────────────────────────────

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    color: TC.primary, fontSize: FS.md,
    background: "#ffffff",
    "& fieldset": { borderColor: "rgba(0,87,216,0.2)" },
    "&:hover fieldset": { borderColor: "rgba(0,87,216,0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#0057d8" },
  },
  "& .MuiInputBase-input::placeholder": { color: "rgba(74,96,128,0.5)" },
  "& .MuiInputLabel-root": {
    color: TC.tertiary, fontSize: "0.78rem",
    backgroundColor: "transparent",
    "&.MuiFormLabel-filled, &.Mui-focused": {
      backgroundColor: "#ffffff",
      paddingLeft: "4px",
      paddingRight: "4px",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: TC.link },
};

const SELECT_SX = {
  color: TC.primary, fontSize: FS.md,
  background: "#ffffff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,87,216,0.2)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,87,216,0.4)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0057d8" },
  "& .MuiSelect-icon": { color: TC.secondary },
};

// Step descriptions shown in the context banner
const STEP_META = [
  { emoji: "🏢", action: "Sync Companies House data, verify address, SIC codes and share capital." },
  { emoji: "👤", action: "Review directors and PSC holders — add, edit or record resignations." },
  { emoji: "📧", action: "Send a secure review link to the client and wait for their submission." },
  { emoji: "✅", action: "Review the client's response, accept corrections, preview and approve." },
  { emoji: "📄", action: "File the approved statement with Companies House and confirm receipt." },
];

function StepHeader({ n, title, subtitle }) {
  const meta = STEP_META[n - 1] || {};
  return (
    <VuiBox mb={3} p={2} sx={{
      borderRadius: "12px",
      background: "linear-gradient(135deg, rgba(0,117,255,0.12) 0%, rgba(0,117,255,0.04) 100%)",
      border: "1px solid rgba(0,117,255,0.25)",
    }}>
      <VuiBox display="flex" alignItems="center" gap={1.5}>
        <VuiBox sx={{
          width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
          background: "rgba(0,117,255,0.2)", border: "1px solid rgba(0,117,255,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.2rem",
        }}>
          {meta.emoji}
        </VuiBox>
        <VuiBox>
          <VuiBox display="flex" alignItems="center" gap={1} mb={0.3}>
            <VuiTypography variant="caption" sx={{
              fontSize: "0.6rem", fontWeight: 800, textTransform: "uppercase",
              letterSpacing: "0.12em", color: TC.tertiary,
            }}>
              Step {n} of {STEPS.length}
            </VuiTypography>
            <VuiBox sx={{ width: 1, height: 12, background: "#f4f8ff" }} />
            <VuiTypography variant="caption" sx={{
              fontSize: "0.6rem", fontWeight: FW.bold, textTransform: "uppercase",
              letterSpacing: "0.1em", color: TC.link,
            }}>
              {title}
            </VuiTypography>
          </VuiBox>
          <VuiTypography variant="caption" sx={{ fontSize: FS.md, color: TC.midtone, lineHeight: 1.55 }}>
            {subtitle || meta.action}
          </VuiTypography>
        </VuiBox>
      </VuiBox>
    </VuiBox>
  );
}

function SectionLabel({ children }) {
  return (
    <VuiBox mb={1.5} display="flex" alignItems="center" gap={1}>
      <VuiBox sx={{ width: 3, height: 14, borderRadius: "2px", background: "linear-gradient(180deg,#0075ff,#00d4ff)", flexShrink: 0 }} />
      <VuiTypography variant="caption" fontWeight="bold"
        sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: FS.xs, letterSpacing: "0.08em" }}>
        {children}
      </VuiTypography>
    </VuiBox>
  );
}

/*Bearer ${token}` Shown inside any section that cannot be changed via CS01 — must use a separate CH form */
function JourneyIntro({ profile, madeUpTo, onStart }) {
  const company = profile?.legalName || "this company";
  const number = profile?.companyRegNumber;
  const rows = [
    ["Proposed made-up-to date", madeUpTo ? fmt(madeUpTo) : "To be confirmed"],
    ["Time needed", "About 15 minutes"],
    ["What you will check", "Registered email and office, officers, registers or SAIL, SIC codes, share capital, shareholders and PSCs."],
    ["Companies House fee", "£50 statutory fee — handled outside LedgerHub. LedgerHub does not collect payment."],
  ];
  return (
    <VuiBox sx={{ maxWidth: 900, color: TC.primary }}>
      <VuiTypography component="p" sx={{ color: TC.secondary, fontSize: FS.sm, mb: 1.5 }}>Filing for: {company}{number ? ` (${number})` : ""}</VuiTypography>
      <VuiTypography component="h1" sx={{ color: TC.primary, fontSize: FS.lg, fontWeight: FW.bold, lineHeight: 1.25, mb: 2 }}>Confirmation statement</VuiTypography>
      <VuiTypography component="p" sx={{ color: TC.midtone, fontSize: FS.sm, lineHeight: 1.55, maxWidth: 760, mb: 3 }}>Confirm that the company information held by Companies House is complete and up to date for this filing period. The proposed made-up-to date is the period end for this CS01, not the Companies House filing due date.</VuiTypography>
      <VuiBox role="note" sx={{ background: "rgba(245,158,11,0.1)", borderLeft: `5px solid ${TC.warningAmber}`, borderRadius: "8px", px: 2, py: 1.5, mb: 4 }}>
        <VuiTypography sx={{ color: TC.midtone, fontSize: FS.base, lineHeight: 1.5 }}>Information shown may not include a filing that has recently been submitted to Companies House. You can still prepare this confirmation statement.</VuiTypography>
      </VuiBox>
      <VuiTypography component="h2" sx={{ color: TC.primary, fontSize: FS.lg, fontWeight: FW.bold, mb: 1.5 }}>Before you start</VuiTypography>
      <VuiBox sx={{ borderTop: `1px solid ${TC.tertiary}`, mb: 3 }}>
        {rows.map(([label, value]) => <VuiBox key={label} sx={{ borderBottom: `1px solid ${TC.tertiary}`, py: 1.6 }}>
          <VuiTypography sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.base, mb: .35 }}>{label}</VuiTypography>
          <VuiTypography sx={{ color: TC.midtone, fontSize: FS.base, lineHeight: 1.5 }}>{value}</VuiTypography>
        </VuiBox>)}
      </VuiBox>
      <VuiButton color="info" variant="contained" onClick={onStart} sx={{ px: 3, py: 1.2, fontSize: FS.base }}>Start</VuiButton>
    </VuiBox>
  );
}

function TradingQuestion({ profile, madeUpTo, value, onChange, onContinue, onBack }) {
  return (
    <VuiBox sx={{ maxWidth: 900, color: TC.primary }}>
      <VuiTypography component="p" sx={{ color: TC.secondary, fontSize: FS.sm, mb: 1.5 }}>Filing for: {profile?.legalName || "this company"}{profile?.companyRegNumber ? ` (${profile.companyRegNumber})` : ""}</VuiTypography>
      <VuiTypography component="h1" sx={{ color: TC.primary, fontSize: FS.lg, fontWeight: FW.bold, mb: 1.5 }}>Confirmation statement</VuiTypography>
      <VuiTypography component="h2" sx={{ color: TC.primary, fontSize: FS.base, fontWeight: FW.semibold, mb: 1 }}>Proposed made-up-to date: {fmt(madeUpTo)}</VuiTypography>
      <Divider sx={{ my: 2, borderColor: TC.tertiary }} />
      <VuiTypography component="h2" sx={{ color: TC.primary, fontSize: FS.base, fontWeight: FW.semibold, mb: 1 }}>Company trading status</VuiTypography>
      <VuiTypography sx={{ color: TC.midtone, fontSize: FS.sm, lineHeight: 1.5, mb: 1.5 }}>Were any of the company&apos;s shares admitted to trading on a market at any time during this confirmation statement period?</VuiTypography>
      <VuiBox role="radiogroup" aria-label="Company trading status" display="flex" flexDirection="column" gap={1}>
        {[
          ["yes", "Yes"],
          ["no", "No"],
        ].map(([optionValue, label]) => (
          <VuiBox
            key={optionValue}
            component="button"
            type="button"
            onClick={() => onChange(optionValue)}
            role="radio"
            aria-checked={value === optionValue}
            sx={{
              m: 0,
              px: 1.25,
              py: 0.6,
              width: { xs: "100%", sm: 180 },
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              textAlign: "left",
              border: `1px solid ${value === optionValue ? TC.link : "rgba(148,163,184,0.45)"}`,
              borderRadius: "8px",
              background: value === optionValue ? "rgba(37,99,235,0.08)" : TC.white,
              color: TC.primary,
              "&:focus-visible": { outline: `2px solid ${TC.link}`, outlineOffset: 2 },
              "&:hover": { borderColor: TC.link, background: "rgba(37,99,235,0.05)" },
            }}
          >
            <VuiBox sx={{
              width: 18,
              height: 18,
              flexShrink: 0,
              borderRadius: "50%",
              border: `2px solid ${value === optionValue ? TC.link : TC.secondary}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              {value === optionValue && <VuiBox sx={{ width: 8, height: 8, borderRadius: "50%", background: TC.link }} />}
            </VuiBox>
            <VuiTypography sx={{ fontSize: FS.sm, fontWeight: value === optionValue ? FW.semibold : FW.normal }}>
              {label}
            </VuiTypography>
          </VuiBox>
        ))}
      </VuiBox>
      {!value && <VuiTypography role="alert" sx={{ color: TC.error, fontSize: FS.md, mt: 1 }}>Select yes or no to continue.</VuiTypography>}
      <VuiBox display="flex" gap={2} mt={4} alignItems="center">
        <VuiButton color="info" variant="contained" disabled={!value} onClick={onContinue} sx={{ px: 2.5 }}>Continue</VuiButton>
        <VuiButton color="info" variant="text" onClick={onBack}>Back</VuiButton>
      </VuiBox>
    </VuiBox>
  );
}

function ReviewConfirmations({ profile, directors }) {
  const [open, setOpen] = useState("registered-email");
  const [answers, setAnswers] = useState({});
  const address = [profile?.registeredAddressObj?.addressLine1, profile?.registeredAddressObj?.locality, profile?.registeredAddressObj?.postalCode].filter(Boolean).join(", ") || profile?.registeredAddress || "No registered office recorded";
  const sections = [
    ["registered-email", "Registered email", profile?.contactEmail || "No registered email recorded", false, "Shown for reference. Update the registered email through the appropriate Companies House filing."],
    ["registered-office", "Registered office", address, false, "Shown for reference. A registered office change must be filed separately using AD01."],
    ["officers", "Officers", `${(directors || []).filter(d => !d.resignedOn).length} active officer(s) recorded`, false, "Shown for reference. Officer changes require the relevant appointment or termination form."],
    ["registers", "Registers and SAIL", profile?.sailAddress || "No SAIL address recorded", false, "Shown for reference. Register or SAIL changes must be filed separately."],
    ["shareholders", "Shareholders", "Review the shareholding details below.", true, "To make a change, update the shareholder information in the LedgerHub company-details section below, then confirm the updated details."],
    ["pscs", "People with significant control (PSCs)", `${(profile?.pscs || []).filter(p => !p.ceasedOn).length} active PSC(s) recorded`, false, "Shown for reference. PSC changes require the relevant PSC form before filing."],
  ];
  return (
    <VuiBox mb={3} sx={{ maxWidth: 960 }}>
      <VuiBox role="note" sx={{ background: "rgba(245,158,11,0.1)", borderLeft: `5px solid ${TC.warningAmber}`, borderRadius: "8px", px: 2, py: 1.25, mb: 2 }}>
        <VuiTypography sx={{ color: TC.midtone, fontSize: FS.base }}>Source: LedgerHub profile and latest Companies House sync. Recent filings may not yet be reflected. Sync or submit the relevant Companies House update before filing.</VuiTypography>
      </VuiBox>
      {sections.map(([key, title, detail, canConfirm, changeGuidance]) => {
        const expanded = key === open;
        return <VuiBox key={key} sx={{ border: `1px solid ${TC.tertiary}`, borderRadius: "8px", mb: 1, background: TC.white, overflow: "hidden" }}>
          <VuiBox component="button" type="button" onClick={() => setOpen(expanded ? "" : key)} aria-expanded={expanded}
            sx={{ width: "100%", border: 0, cursor: "pointer", textAlign: "left", background: expanded ? "rgba(37,99,235,0.1)" : "rgba(148,163,184,0.1)", color: TC.link, p: 1.5, fontWeight: FW.bold, fontSize: FS.base, "&:hover": { background: "rgba(37,99,235,0.14)" }, "&:focus-visible": { outline: `2px solid ${TC.link}`, outlineOffset: -2 } }}>
            {expanded ? "▼" : "▶"}&nbsp; {title}
          </VuiBox>
          <Collapse in={expanded}>
            <VuiBox p={2}>
              <VuiTypography sx={{ color: TC.midtone, mb: 1.25, fontSize: FS.base }}>{detail}</VuiTypography>
              {canConfirm ? (
                <>
                  <VuiTypography sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.base }}>Is this information correct?</VuiTypography>
                  <RadioGroup value={answers[key] || ""} onChange={(event) => setAnswers(prev => ({ ...prev, [key]: event.target.value }))} aria-label={`${title} confirmation`}>
                    <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                    <FormControlLabel value="update-submitted" control={<Radio size="small" />} label="No, but an update has been submitted" />
                  </RadioGroup>
                  <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm, lineHeight: 1.5, mt: 0.5 }}>
                    {changeGuidance}
                  </VuiTypography>
                </>
              ) : (
                <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm, lineHeight: 1.5 }}>
                  {changeGuidance}
                </VuiTypography>
              )}
            </VuiBox>
          </Collapse>
        </VuiBox>;
      })}
    </VuiBox>
  );
}

function SeparateFilingBanner({ form, reason }) {
  return (
    <VuiBox p={1.2} mb={1.5} sx={{
      borderRadius: "10px",
      background: "rgba(255,152,0,0.07)",
      border: "1px solid rgba(255,152,0,0.35)",
      display: "flex", alignItems: "flex-start", gap: 1,
    }}>
      <LockIcon sx={{ color: TC.warning, fontSize: "0.95rem", mt: "1px", flexShrink: 0 }} />
      <VuiBox>
        <VuiTypography variant="caption" sx={{ color: TC.warning, fontWeight: FW.bold, fontSize: FS.sm, display: "block" }}>
          Cannot be changed via CS01
        </VuiTypography>
        <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem", lineHeight: 1.5 }}>
          {reason} To make changes, file form <strong style={{ color: "#ffb74d" }}>{form}</strong> with Companies House first, then re-sync.
        </VuiTypography>
      </VuiBox>
    </VuiBox>
  );
}

function SaveBar({ saving, saved, error, onSave, onReset, dirty, label = "Save Changes" }) {
  return (
    <VuiBox display="flex" alignItems="center" gap={1} mt={1.5} flexWrap="wrap">
      <VuiButton size="small" color="info" variant="contained" onClick={onSave} disabled={saving || !dirty}
        startIcon={saving ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : <SaveIcon sx={{ fontSize: "13px !important" }} />}
        sx={{ fontSize: FS.sm }}>
        {saving ? "Saving…" : label}
      </VuiButton>
      {dirty && !saving && (
        <VuiButton size="small" color="secondary" variant="outlined" onClick={onReset}
          sx={{ fontSize: FS.sm, bordercolor: "#4a6080", color: TC.primary, "&:hover": { borderColor: "white", color: TC.primary } }}>
          Reset
        </VuiButton>
      )}
      {saved && !dirty && (
        <VuiTypography variant="caption" sx={{ color: TC.success, fontSize: FS.sm }}>✓ Saved</VuiTypography>
      )}
      {error && (
        <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: FS.sm }}>{error}</VuiTypography>
      )}
    </VuiBox>
  );
}

// ─── Sidebar Navigation (TaxCalc-style) ──────────────────────────────────────

const WORKFLOW_STEPS = [
  { n: 1, icon: "🏢", title: "Company Data",     desc: "Pull latest data from Companies House. Verify SIC codes and statement of capital." },
  { n: 2, icon: "👤", title: "Officers & PSC",   desc: "Review directors and PSCs. Changes require separate CH forms (AP01, PSC01, etc.)." },
  { n: 3, icon: "📧", title: "Customer Review",  desc: "Send the client a secure link to review company details and confirm or flag corrections." },
  { n: 4, icon: "✅", title: "Agent Approval",   desc: "Review the client's response, apply accepted corrections, then approve the statement." },
  { n: 5, icon: "📄", title: "Receipt & Filing", desc: "File the approved CS01 with Companies House and track submission status." },
];

function StepperBar({ step, sectionStatus, onSelect }) {
  return (
    <VuiBox mb={3} p={2} sx={{ borderRadius: "12px", background: TC.white, border: "1px solid rgba(37,99,235,0.18)" }}>
      <VuiBox display="flex" alignItems="baseline" justifyContent="space-between" mb={1.5}>
        <VuiTypography sx={{ color: TC.primary, fontSize: FS.base, fontWeight: FW.bold }}>
          Filing progress
        </VuiTypography>
        <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm }}>
          Step {step + 1} of {STEPS.length}
        </VuiTypography>
      </VuiBox>
      <VuiBox sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 0.8 }}>
        {STEPS.map((s, idx) => {
          const { dot = "rgba(148,163,184,0.35)", sublabel = "" } = sectionStatus[idx] || {};
          const isActive = idx === step;
          const isCompleted = dot === "#4caf50";

          return (
            <VuiBox
              key={s.label}
              component="button"
              type="button"
              onClick={() => onSelect(idx)}
              aria-current={isActive ? "step" : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
                p: 1.1,
                cursor: "pointer",
                textAlign: "left",
                background: isActive ? "rgba(37,99,235,0.09)" : TC.white,
                border: `1px solid ${isActive ? TC.link : "rgba(148,163,184,0.28)"}`,
                borderRadius: "8px",
                color: TC.primary,
                "&:hover": { borderColor: TC.link, background: "rgba(37,99,235,0.05)" },
                "&:focus-visible": { outline: `2px solid ${TC.link}`, outlineOffset: 2 },
              }}
            >
              <VuiBox sx={{
                width: 24,
                height: 24,
                flexShrink: 0,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: isActive ? TC.link : isCompleted ? TC.success : "rgba(148,163,184,0.16)",
                color: isActive || isCompleted ? TC.white : TC.secondary,
                fontSize: FS.sm,
                fontWeight: FW.bold,
              }}>
                {isCompleted ? "✓" : idx + 1}
              </VuiBox>
              <VuiBox minWidth={0}>
                <VuiTypography sx={{ fontSize: FS.sm, fontWeight: isActive ? FW.bold : FW.medium, color: isActive ? TC.link : TC.primary, lineHeight: 1.25 }}>
                  {s.label}
                </VuiTypography>
                {sublabel && (
                  <VuiTypography sx={{ fontSize: FS.xs, color: isCompleted ? TC.success : TC.tertiary, mt: 0.2, lineHeight: 1.2 }}>
                    {sublabel}
                  </VuiTypography>
                )}
              </VuiBox>
            </VuiBox>
          );
        })}
      </VuiBox>
    </VuiBox>
  );
}

// ─── CS Info Card (sidebar) ───────────────────────────────────────────────────

function CsInfoCard({ profile, madeUpTo }) {
  const nextDue    = profile?.confirmationStatementNextDue;
  const lastMadeUp = profile?.confirmationStatementLastMadeUpTo;
  const days       = daysUntil(nextDue);

  const dueBg    = days === null  ? "#f4f8ff"
                 : days < 0      ? "rgba(211,47,47,0.07)"
                 : days <= 14    ? "rgba(255,109,0,0.07)"
                 : days <= 30    ? "rgba(255,152,0,0.07)"
                 :                 "rgba(56,142,60,0.07)";
  const dueBorder = days === null ? "#d8e6f7"
                 : days < 0      ? "rgba(211,47,47,0.4)"
                 : days <= 14    ? "rgba(255,109,0,0.4)"
                 : days <= 30    ? "rgba(255,152,0,0.35)"
                 :                 "rgba(56,142,60,0.3)";
  const dueColor  = days === null ? TC.secondary
                 : days < 0      ? "#c62828"
                 : days <= 14    ? "#e65100"
                 : days <= 30    ? "#ef6c00"
                 :                 "#2e7d32";
  const dueLabel  = days === null ? "—"
                 : days < 0      ? `Overdue by ${Math.abs(days)}d`
                 : days === 0    ? "Due today"
                 : days === 1    ? "Due tomorrow"
                 :                 `Due in ${days}d`;

  const rows = [
    { label: "Next Due",     value: fmt(nextDue),    extra: nextDue ? dueLabel : null, extraColor: dueColor },
    { label: "Last Made Up", value: fmt(lastMadeUp) },
    { label: "Period End",   value: madeUpTo ? fmt(madeUpTo) : "—" },
    { label: "Status",       value: profile?.companyStatus ? profile.companyStatus.charAt(0).toUpperCase() + profile.companyStatus.slice(1) : "—" },
    { label: "Reg No.",      value: profile?.companyRegNumber || "—" },
  ];

  return (
    <VuiBox mt={2} p={1.6} sx={{ borderRadius: "10px", background: dueBg, border: `1px solid ${dueBorder}` }}>
      <VuiTypography variant="caption" sx={{
        fontSize: "0.6rem", fontWeight: FW.bold, color: TC.link,
        textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1.2,
      }}>
        📋 CS01 Summary
      </VuiTypography>

      {rows.map(({ label, value, extra, extraColor }) => (
        <VuiBox key={label} display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.9}>
          <VuiTypography variant="caption" sx={{ fontSize: "0.68rem", color: TC.tertiary, fontWeight: FW.medium, lineHeight: 1.4, flexShrink: 0, mr: 0.5 }}>
            {label}
          </VuiTypography>
          <VuiBox textAlign="right">
            <VuiTypography variant="caption" sx={{ fontSize: "0.72rem", color: TC.primary, fontWeight: FW.semibold, lineHeight: 1.4 }}>
              {value}
            </VuiTypography>
            {extra && (
              <VuiTypography variant="caption" sx={{ fontSize: "0.62rem", color: extraColor, fontWeight: FW.bold, display: "block", lineHeight: 1.3 }}>
                {extra}
              </VuiTypography>
            )}
          </VuiBox>
        </VuiBox>
      ))}
    </VuiBox>
  );
}

// ─── Step 0: Company Data ─────────────────────────────────────────────────────

function ShareClassRow({ cls, index, onChange, onRemove }) {
  const totalNominal = (() => {
    const n = parseFloat(cls.numShares) || 0;
    const v = parseFloat(cls.nominalValuePerShare) || 0;
    return (n * v).toFixed(2);
  })();
  return (
    <VuiBox mb={1.2} p={1.2} sx={DARK_CARD_INNER}>
      <Grid container spacing={1} alignItems="flex-start">
        <Grid item xs={12} sm={3}>
          <TextField fullWidth size="small" label="Class name" placeholder="e.g. Ordinary"
            value={cls.name || ""} onChange={(e) => onChange(index, "name", e.target.value)}
            InputLabelProps={{ shrink: true }} sx={INPUT_SX} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField fullWidth size="small" label="No. of shares" type="number"
            value={cls.numShares || ""} onChange={(e) => onChange(index, "numShares", e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ style: { color: TC.primary, fontSize: "0.8rem" }, min: 0 }} sx={INPUT_SX} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField fullWidth size="small" label="Nominal value" placeholder="e.g. 0.01"
            value={cls.nominalValuePerShare || ""} onChange={(e) => onChange(index, "nominalValuePerShare", e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ style: { color: TC.primary, fontSize: "0.8rem" } }} sx={INPUT_SX} />
        </Grid>
        <Grid item xs={9} sm={4}>
          <TextField fullWidth size="small" label="Prescribed particulars (rights)"
            placeholder="e.g. Standard voting, dividend, capital rights"
            value={cls.prescribedParticulars || ""} onChange={(e) => onChange(index, "prescribedParticulars", e.target.value)}
            InputLabelProps={{ shrink: true }} sx={INPUT_SX} />
        </Grid>
        <Grid item xs={3} sm={1} display="flex" alignItems="center" justifyContent="flex-end">
          <Tooltip title="Remove class">
            <span>
              <VuiButton size="small" color="secondary" variant="outlined" onClick={() => onRemove(index)}
                sx={{ minWidth: 30, p: 0.4, fontSize: FS.xs }}>
                <CloseIcon sx={{ fontSize: FS.md }} />
              </VuiButton>
            </span>
          </Tooltip>
        </Grid>
        {(cls.numShares && cls.nominalValuePerShare) && (
          <Grid item xs={12}>
            <VuiTypography variant="caption" sx={{ color: TC.link, fontSize: FS.xs }}>
              Aggregate nominal value: £{totalNominal}
            </VuiTypography>
          </Grid>
        )}
      </Grid>
    </VuiBox>
  );
}

// ─── CS01 Preview Modal ───────────────────────────────────────────────────────

function PreviewSection({ title, children }) {
  return (
    <VuiBox mb={2.5}>
      <VuiTypography variant="caption" sx={{ fontSize: FS.xs, fontWeight: FW.bold, color: TC.link, textTransform: "uppercase", letterSpacing: "0.09em", display: "block", mb: 1 }}>
        {title}
      </VuiTypography>
      <VuiBox p={1.8} sx={{ borderRadius: "10px", background: "#ffffff", border: "1px solid #d8e6f7", boxShadow: "0 1px 4px rgba(0,50,120,0.06)" }}>
        {children}
      </VuiBox>
    </VuiBox>
  );
}

function PreviewModal({ open, onClose, profile, directors, madeUpTo }) {
  const addr       = profile?.registeredAddressObj;
  const activeDirs = (directors || []).filter(d => !d.resignedOn);
  const activePscs = (profile?.pscs || []).filter(p => !p.ceasedOn);
  const sc         = profile?.shareCapital;
  const totalShares = sc?.shareClasses?.reduce((s, c) => s + (parseInt(c.numShares) || 0), 0) || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper"
      PaperProps={{ sx: {
        background: "#ffffff",
        border: "none", borderRadius: "16px", maxHeight: "88vh",
        boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
      }}}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1.5, pt: 2, borderBottom: "1px solid #e0e7ef", background: "#f0f6ff" }}>
        <VuiBox display="flex" alignItems="center" gap={1.5}>
          <AssignmentTurnedInIcon sx={{ color: TC.link, fontSize: "1.2rem" }} />
          <VuiBox>
            <VuiTypography sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.base, lineHeight: 1.2 }}>
              CS01 Preview
            </VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
              {profile?.legalName}{profile?.companyRegNumber ? ` · ${profile.companyRegNumber}` : ""}{madeUpTo ? ` · Period ending ${madeUpTo}` : ""}
            </VuiTypography>
          </VuiBox>
        </VuiBox>
        <IconButton onClick={onClose} size="small" sx={{ color: TC.secondary, "&:hover": { color: TC.primary, background: "rgba(0,0,0,0.06)" } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, pb: 3, background: "#f8fafd" }}>

        {/* Company Identity */}
        <PreviewSection title="🏢 Company Identity">
          <Grid container spacing={1.5}>
            {[
              { label: "Legal Name",   value: profile?.legalName },
              { label: "Trading Name", value: profile?.tradingName },
              { label: "Reg Number",   value: profile?.companyRegNumber },
              { label: "Status",       value: profile?.companyStatus },
              { label: "Type",         value: profile?.companyTypeRaw },
              { label: "Incorporated", value: fmt(profile?.incorporationDate) },
            ].filter(f => f.value).map(({ label, value }) => (
              <Grid item xs={6} sm={4} key={label}>
                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block", mb: 0.2 }}>{label}</VuiTypography>
                <VuiTypography variant="caption" sx={{ color: value === "active" ? "#1a7f37" : "#0a1628", fontSize: FS.md, fontWeight: FW.bold }}>{value}</VuiTypography>
              </Grid>
            ))}
          </Grid>
        </PreviewSection>

        {/* Registered Address */}
        <PreviewSection title="📍 Registered Address">
          {addr?.addressLine1 ? (
            <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.md }}>
              {[addr.addressLine1, addr.addressLine2, addr.locality, addr.region, addr.postalCode, addr.country].filter(Boolean).map((line, i) => (
                <span key={i} style={{ display: "block", lineHeight: 2, fontWeight: FW.medium }}>{line}</span>
              ))}
            </VuiTypography>
          ) : (
            <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.78rem" }}>No address on record — sync from Companies House</VuiTypography>
          )}
        </PreviewSection>

        {/* SIC Codes */}
        <PreviewSection title="🏭 SIC Codes (Business Activities)">
          {(profile?.sicCodes?.length > 0) ? (
            <VuiBox display="flex" gap={0.8} flexWrap="wrap">
              {profile.sicCodes.map(code => (
                <Chip key={code} label={code} size="small"
                  sx={{ bgcolor: "#e8f0fe", color: TC.link, border: "1px solid #bbd0f8", fontSize: "0.78rem", height: 26, fontWeight: FW.bold }} />
              ))}
            </VuiBox>
          ) : (
            <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.78rem" }}>No SIC codes on record</VuiTypography>
          )}
        </PreviewSection>

        {/* Statement of Capital */}
        <PreviewSection title="💰 Statement of Capital">
          {sc?.shareClasses?.length > 0 ? (
            <>
              <VuiBox mb={1.5} display="flex" gap={4}>
                <VuiBox>
                  <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block", mb: 0.2 }}>Total Shares</VuiTypography>
                  <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.95rem", fontWeight: FW.bold }}>{totalShares.toLocaleString()}</VuiTypography>
                </VuiBox>
                <VuiBox>
                  <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block", mb: 0.2 }}>Currency</VuiTypography>
                  <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.95rem", fontWeight: FW.bold }}>{sc.currency || "GBP"}</VuiTypography>
                </VuiBox>
              </VuiBox>
              {sc.shareClasses.map((cls, i) => (
                <VuiBox key={i} mb={0.8} display="flex" gap={2} flexWrap="wrap" px={1.4} py={1}
                  sx={{ borderRadius: "8px", background: "#f4f8ff", border: "1px solid #d8e6f7" }}>
                  <VuiBox><VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.62rem", display: "block", mb: 0.2 }}>Class</VuiTypography><VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.md, fontWeight: FW.bold }}>{cls.name || "—"}</VuiTypography></VuiBox>
                  <VuiBox><VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.62rem", display: "block", mb: 0.2 }}>No. of Shares</VuiTypography><VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.md, fontWeight: FW.bold }}>{(parseInt(cls.numShares) || 0).toLocaleString()}</VuiTypography></VuiBox>
                  <VuiBox><VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.62rem", display: "block", mb: 0.2 }}>Nominal Value</VuiTypography><VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.md, fontWeight: FW.bold }}>£{cls.nominalValuePerShare || "0"}</VuiTypography></VuiBox>
                  {cls.prescribedParticulars && <VuiBox flex={1}><VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.62rem", display: "block", mb: 0.2 }}>Rights</VuiTypography><VuiTypography variant="caption" sx={{ color: "#253047", fontSize: FS.sm }}>{cls.prescribedParticulars}</VuiTypography></VuiBox>}
                </VuiBox>
              ))}
            </>
          ) : (
            <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.78rem" }}>No share classes recorded</VuiTypography>
          )}
        </PreviewSection>

        {/* Directors */}
        <PreviewSection title={`👤 Active Directors (${activeDirs.length})`}>
          {activeDirs.length === 0 ? (
            <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.78rem" }}>No active directors on record</VuiTypography>
          ) : activeDirs.map((d, i) => (
            <VuiBox key={d.id || i} mb={0.8} display="flex" gap={2} flexWrap="wrap" px={1.4} py={1}
              sx={{ borderRadius: "8px", background: "#f4f8ff", border: "1px solid #d8e6f7" }}>
              <VuiBox flex={1}>
                <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.md, fontWeight: FW.bold }}>{d.fullName}</VuiTypography>
                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mt: 0.2 }}>
                  {d.role?.replace(/_/g, " ")}{d.appointedOn ? ` · Appointed ${fmt(d.appointedOn)}` : ""}
                </VuiTypography>
              </VuiBox>
              {d.nationality && <VuiBox><VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.62rem", display: "block", mb: 0.2 }}>Nationality</VuiTypography><VuiTypography variant="caption" sx={{ color: "#253047", fontSize: FS.sm, fontWeight: FW.semibold }}>{d.nationality}</VuiTypography></VuiBox>}
              {d.occupation && <VuiBox><VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.62rem", display: "block", mb: 0.2 }}>Occupation</VuiTypography><VuiTypography variant="caption" sx={{ color: "#253047", fontSize: FS.sm, fontWeight: FW.semibold }}>{d.occupation}</VuiTypography></VuiBox>}
            </VuiBox>
          ))}
        </PreviewSection>

        {/* PSCs */}
        <PreviewSection title={`🔑 Persons with Significant Control (${activePscs.length} active)`}>
          {activePscs.length === 0 ? (
            <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.78rem" }}>No active PSCs on record</VuiTypography>
          ) : activePscs.map((p, i) => (
            <VuiBox key={p.id || i} mb={0.8} px={1.4} py={1}
              sx={{ borderRadius: "8px", background: "#f4f8ff", border: "1px solid #d8e6f7" }}>
              <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.md, fontWeight: FW.bold }}>{p.name || "—"}</VuiTypography>
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mt: 0.2 }}>
                {PSC_KINDS.find(k => k.value === p.kind)?.label || "Individual"}
                {p.notifiedOn ? ` · Notified ${fmt(p.notifiedOn)}` : ""}
              </VuiTypography>
              {(p.naturesOfControl?.length > 0) && (
                <VuiBox mt={0.7} display="flex" gap={0.5} flexWrap="wrap">
                  {p.naturesOfControl.map(n => (
                    <Chip key={n} label={NATURES_OF_CONTROL.find(x => x.value === n)?.label || n} size="small"
                      sx={{ height: 22, fontSize: FS.xs, bgcolor: "#e6f4ea", color: "#1a7f37", border: "1px solid #a8d5b5", fontWeight: FW.bold }} />
                  ))}
                </VuiBox>
              )}
            </VuiBox>
          ))}
        </PreviewSection>

      </DialogContent>
    </Dialog>
  );
}

// ─── Step 0: Company Data ─────────────────────────────────────────────────────

function StepCompanyData({ profile, tenantId, clientId, onProfileUpdated, onDirectorsUpdated }) {
  const initCompany = () => ({
    tradingName: profile.tradingName || "",
    sicInput:    "",
    sicCodes:    profile.sicCodes ? [...profile.sicCodes] : [],
  });
  const initCapital = () => ({
    currency:     profile.shareCapital?.currency     || "GBP",
    shareClasses: profile.shareCapital?.shareClasses ? JSON.parse(JSON.stringify(profile.shareCapital.shareClasses)) : [],
  });

  const [company, setCompany]         = useState(initCompany);
  const [capital, setCapital]         = useState(initCapital);
  const [companyDirty, setCompanyDirty] = useState(false);
  const [capitalDirty, setCapitalDirty] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingCapital, setSavingCapital] = useState(false);
  const [savedCompany, setSavedCompany]   = useState(false);
  const [savedCapital, setSavedCapital]   = useState(false);
  const [companyErr, setCompanyErr]       = useState("");
  const [capitalErr, setCapitalErr]       = useState("");
  const [syncing, setSyncing]             = useState(false);
  const [syncMsg,  setSyncMsg]            = useState("");

  useEffect(() => { setCompany(initCompany()); setCapital(initCapital()); setCompanyDirty(false); setCapitalDirty(false); setSavedCompany(false); setSavedCapital(false); }, [profile.id]);

  const setC = (k, v) => { setCompany(f => ({ ...f, [k]: v })); setCompanyDirty(true); setSavedCompany(false); };

  const addSic = () => {
    const code = company.sicInput.trim().replace(/\D/g, "");
    if (!code || company.sicCodes.includes(code)) return;
    setC("sicCodes", [...company.sicCodes, code]);
    setCompany(f => ({ ...f, sicInput: "" }));
  };

  const saveCompany = async () => {
    setSavingCompany(true); setCompanyErr("");
    try {
      await updateClientProfile(tenantId, clientId, {
        tradingName: company.tradingName || null,
        sicCodes: company.sicCodes,
      });
      setSavedCompany(true); setCompanyDirty(false);
      if (onProfileUpdated) onProfileUpdated();
    } catch (e) { setCompanyErr(e?.message || "Save failed."); }
    finally { setSavingCompany(false); }
  };

  const updateShareClass = (i, k, v) => {
    const updated = capital.shareClasses.map((c, idx) => idx === i ? { ...c, [k]: v } : c);
    setCapital(f => ({ ...f, shareClasses: updated })); setCapitalDirty(true); setSavedCapital(false);
  };
  const addShareClass = () => {
    setCapital(f => ({ ...f, shareClasses: [...f.shareClasses, { id: Date.now().toString(), name: "", numShares: "", nominalValuePerShare: "", prescribedParticulars: "" }] }));
    setCapitalDirty(true); setSavedCapital(false);
  };
  const removeShareClass = (i) => {
    setCapital(f => ({ ...f, shareClasses: f.shareClasses.filter((_, idx) => idx !== i) }));
    setCapitalDirty(true); setSavedCapital(false);
  };
  const saveCapital = async () => {
    setSavingCapital(true); setCapitalErr("");
    try {
      await updateClientProfile(tenantId, clientId, { shareCapital: { currency: capital.currency, shareClasses: capital.shareClasses } });
      setSavedCapital(true); setCapitalDirty(false);
      if (onProfileUpdated) onProfileUpdated();
    } catch (e) { setCapitalErr(e?.message || "Save failed."); }
    finally { setSavingCapital(false); }
  };

  const handleSync = async () => {
    setSyncing(true); setSyncMsg("");
    try {
      await resyncClientFromCH(tenantId, clientId);
      setSyncMsg("✓ Synced from Companies House");
      if (onProfileUpdated)    onProfileUpdated();
      if (onDirectorsUpdated)  onDirectorsUpdated();
      setTimeout(() => setSyncMsg(""), 4000);
    } catch (e) { setSyncMsg(`Sync failed: ${e?.message || "unknown error"}`); }
    finally { setSyncing(false); }
  };

  const days = daysUntil(profile.confirmationStatementNextDue);
  let urgencyColor = "#4caf50", urgencyLabel = "On track";
  if (days !== null) {
    if      (days < 0)   { urgencyColor = "#f44336"; urgencyLabel = `${Math.abs(days)}d overdue`; }
    else if (days <= 30) { urgencyColor = "#ff9800"; urgencyLabel = `${days}d remaining`; }
    else if (days <= 60) { urgencyColor = "#ffc107"; urgencyLabel = `${days}d remaining`; }
    else                 { urgencyLabel = `${days}d remaining`; }
  }

  const totalShares = capital.shareClasses.reduce((s, c) => s + (parseInt(c.numShares) || 0), 0);

  return (
    <VuiBox>
      {/* CH Sync */}
      <VuiBox mb={2} p={1.5} sx={{ ...DARK_CARD, border: "1px solid rgba(0,117,255,0.25)", background: "rgba(0,117,255,0.06)" }}>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <VuiBox>
            <SectionLabel>🔄 Companies House Sync</SectionLabel>
            <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: FS.sm, display: "block" }}>
              Last synced: <strong style={{ color: TC.primary }}>{profile.chLastSyncedAt ? fmt(profile.chLastSyncedAt) : "Never"}</strong>
            </VuiTypography>
            {syncMsg && (
              <VuiTypography variant="caption" sx={{ fontSize: FS.sm, color: syncMsg.startsWith("✓") ? "#4caf50" : "#f44336", display: "block", mt: 0.3 }}>
                {syncMsg}
              </VuiTypography>
            )}
          </VuiBox>
          <VuiButton size="small" color="info" variant="contained" onClick={handleSync} disabled={syncing || !profile.companyRegNumber}
            startIcon={syncing ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : <SyncIcon sx={{ fontSize: "14px !important" }} />}
            sx={{ fontSize: FS.sm }}>
            {syncing ? "Syncing…" : "Sync from CH"}
          </VuiButton>
        </VuiBox>
        {!profile.companyRegNumber && (
          <VuiTypography variant="caption" sx={{ color: TC.warning, fontSize: "0.68rem", display: "block", mt: 0.6 }}>
            ⚠ Add a Companies House registration number below before syncing.
          </VuiTypography>
        )}
      </VuiBox>

      {/* SIC codes */}
      <VuiBox mb={2} p={1.5} sx={DARK_CARD}>
        <SectionLabel>🏭 SIC Codes (Principal Business Activities)</SectionLabel>
        <VuiBox display="flex" gap={0.8} flexWrap="wrap" mb={company.sicCodes.length ? 1 : 0}>
          {company.sicCodes.map((c) => (
            <Chip key={c} label={c} size="small" onDelete={() => setC("sicCodes", company.sicCodes.filter(x => x !== c))}
              deleteIcon={<CloseIcon sx={{ fontSize: "0.7rem !important", color: "rgba(255,255,255,0.5) !important" }} />}
              sx={{ bgcolor: "rgba(0,117,255,0.12)", color: TC.link, border: "1px solid rgba(0,117,255,0.3)", fontSize: FS.sm, height: 22 }} />
          ))}
        </VuiBox>
        <VuiBox display="flex" gap={1} alignItems="center">
          <TextField size="small" placeholder="e.g. 69201" value={company.sicInput}
            onChange={(e) => setCompany(f => ({ ...f, sicInput: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && addSic()}
            inputProps={{ style: { color: TC.primary, fontSize: "0.8rem" }, maxLength: 6 }}
            sx={{ width: 130, ...INPUT_SX }} />
          <VuiButton size="small" color="info" variant="outlined" onClick={addSic}
            startIcon={<AddIcon sx={{ fontSize: "13px !important" }} />} sx={{ fontSize: "0.7rem", minWidth: 80 }}>
            Add SIC
          </VuiButton>
        </VuiBox>
      </VuiBox>

      <SaveBar saving={savingCompany} saved={savedCompany} error={companyErr} dirty={companyDirty}
        onSave={saveCompany}
        onReset={() => { setCompany(initCompany()); setCompanyDirty(false); setCompanyErr(""); }}
        label="Save Company Changes" />

      <Divider sx={{ bordercolor: "#7a94b0", my: 2.5 }} />

      {/* Statement of Capital */}
      <VuiBox p={1.5} sx={DARK_CARD}>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1.2}>
          <VuiBox>
            <SectionLabel>💰 Statement of Capital</SectionLabel>
        <VuiBox p={1} mb={1.2} sx={{ borderRadius: "8px", background: "rgba(0,117,255,0.07)", border: "1px solid rgba(0,117,255,0.2)", display: "flex", alignItems: "flex-start", gap: 0.8 }}>
          <CheckCircleOutlineIcon sx={{ color: TC.linkHover, fontSize: "0.9rem", mt: "1px", flexShrink: 0 }} />
          <VuiTypography variant="caption" sx={{ fontSize: "0.68rem", color: TC.secondary, lineHeight: 1.5 }}>
            You can <strong style={{ color: TC.link }}>correct errors</strong> in existing share classes via CS01. To issue <strong style={{ color: TC.link }}>new shares</strong>, file form <strong style={{ color: "#ffb74d" }}>SH01</strong> first, then re-sync.
          </VuiTypography>
        </VuiBox>
            {totalShares > 0 && (
              <VuiTypography variant="caption" sx={{ color: TC.link, fontSize: "0.7rem" }}>
                Total issued: {totalShares.toLocaleString()} shares across {capital.shareClasses.length} class{capital.shareClasses.length !== 1 ? "es" : ""}
              </VuiTypography>
            )}
          </VuiBox>
          <VuiBox display="flex" alignItems="center" gap={1}>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select value={capital.currency} onChange={(e) => { setCapital(f => ({ ...f, currency: e.target.value })); setCapitalDirty(true); setSavedCapital(false); }}
                displayEmpty sx={{ ...SELECT_SX, fontSize: FS.sm }}
                MenuProps={{ PaperProps: { sx: { bgcolor: "rgba(15,18,59,0.97)", color: TC.primary } } }}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c} sx={{ fontSize: "0.78rem", color: TC.primary }}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </VuiBox>
        </VuiBox>

        {capital.shareClasses.length === 0 ? (
          <VuiBox py={1.5} px={1} sx={{ ...DARK_CARD_INNER, textAlign: "center", mb: 1.2 }}>
            <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: FS.sm }}>
              No share classes added. Click below to add one.
            </VuiTypography>
          </VuiBox>
        ) : (
          capital.shareClasses.map((cls, i) => (
            <ShareClassRow key={cls.id || i} cls={cls} index={i} onChange={updateShareClass} onRemove={removeShareClass} />
          ))
        )}

        <VuiButton size="small" color="info" variant="outlined" onClick={addShareClass}
          startIcon={<AddIcon sx={{ fontSize: "13px !important" }} />} sx={{ fontSize: "0.7rem", mb: 1 }}>
          Add Share Class
        </VuiButton>

        <SaveBar saving={savingCapital} saved={savedCapital} error={capitalErr} dirty={capitalDirty}
          onSave={saveCapital}
          onReset={() => { setCapital(initCapital()); setCapitalDirty(false); setCapitalErr(""); }}
          label="Save Share Capital" />
      </VuiBox>
    </VuiBox>
  );
}

// ─── Step 1: Officers & PSC ───────────────────────────────────────────────────

const EMPTY_DIRECTOR = { fullName: "", role: "director", roleRaw: "", appointedOn: "", resignedOn: "", nationality: "", occupation: "" };

function DirectorRow({ director, tenantId, clientId, onSaved, isNew, lastSnapshotNames, readonly }) {
  const [open,   setOpen]   = useState(isNew && !readonly);
  const [form,   setForm]   = useState({ ...EMPTY_DIRECTOR, ...director });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");
  const isAdded = lastSnapshotNames && !director.resignedOn && lastSnapshotNames.size > 0 && !lastSnapshotNames.has(director.fullName);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.fullName.trim()) { setErr("Full name is required."); return; }
    setSaving(true); setErr("");
    try {
      if (director.id) await updateDirector(tenantId, clientId, director.id, form);
      else await createDirector(tenantId, clientId, form);
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e) { setErr(e?.message || "Save failed."); }
    finally { setSaving(false); }
  };

  return (
    <VuiBox mb={1} sx={{
      borderRadius: "10px", overflow: "hidden",
      border: isAdded ? "1px solid rgba(76,175,80,0.35)" : director.resignedOn ? "1px solid #e4eef8" : "1px solid #d8e6f7",
      background: isAdded ? "rgba(76,175,80,0.06)" : director.resignedOn ? "#f9fbff" : "#f4f8ff",
    }}>
      <VuiBox display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}>
        <VuiBox display="flex" alignItems="center" gap={1} flex={1}>
          <PersonIcon sx={{ color: director.resignedOn ? TC.tertiary : TC.link, fontSize: FS.base }} />
          <VuiBox>
            <VuiBox display="flex" alignItems="center" gap={0.7}>
              <VuiTypography variant="caption" fontWeight="medium" sx={{ color: director.resignedOn ? TC.tertiary : TC.primary, fontSize: "0.8rem", textDecoration: director.resignedOn ? "line-through" : "none" }}>
                {director.fullName || <em style={{ opacity: 0.5 }}>New Director</em>}
              </VuiTypography>
              {isAdded && <Chip label="New" size="small" sx={{ height: 15, fontSize: "0.57rem", fontWeight: FW.bold, bgcolor: "rgba(76,175,80,0.15)", color: TC.success, border: "1px solid rgba(76,175,80,0.4)" }} />}
              {director.resignedOn && <Chip label="Resigned" size="small" sx={{ height: 15, fontSize: "0.57rem", bgcolor: "rgba(244,67,54,0.12)", color: TC.error, border: "1px solid rgba(244,67,54,0.3)" }} />}
            </VuiBox>
            <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.67rem", opacity: 0.75 }}>
              {director.role?.replace(/_/g, " ")}{director.appointedOn ? ` · Appointed ${fmt(director.appointedOn)}` : ""}
              {director.resignedOn ? ` · Resigned ${fmt(director.resignedOn)}` : ""}
            </VuiTypography>
          </VuiBox>
        </VuiBox>
        {!readonly && (
          <VuiButton size="small" color={open ? "secondary" : "info"} variant="outlined" onClick={() => setOpen(!open)}
            startIcon={open ? <CloseIcon sx={{ fontSize: "12px !important" }} /> : <EditIcon sx={{ fontSize: "12px !important" }} />}
            sx={{ fontSize: "0.68rem", minWidth: 70, py: 0.3 }}>
            {open ? "Close" : "Edit"}
          </VuiButton>
        )}
      </VuiBox>
      <Collapse in={open}>
        <Divider sx={{ bordercolor: "#7a94b0" }} />
        <VuiBox p={1.5}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Full Name *" value={form.fullName}
                onChange={(e) => set("fullName", e.target.value)} InputLabelProps={{ shrink: true }} sx={INPUT_SX} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <Select value={form.role} onChange={(e) => set("role", e.target.value)} displayEmpty sx={SELECT_SX}
                  MenuProps={{ PaperProps: { sx: { bgcolor: "rgba(15,18,59,0.97)", color: TC.primary } } }}>
                  {ROLE_OPTIONS.map(r => <MenuItem key={r.value} value={r.value} sx={{ fontSize: FS.md, color: TC.primary, "&:hover": { bgcolor: "rgba(0,117,255,0.15)" } }}>{r.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="date" label="Appointed On" value={form.appointedOn || ""}
                onChange={(e) => set("appointedOn", e.target.value)} InputLabelProps={{ shrink: true }}
                inputProps={{ style: { color: TC.primary, fontSize: "0.8rem" } }} sx={INPUT_SX} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="date" label="Resigned On" value={form.resignedOn || ""}
                onChange={(e) => set("resignedOn", e.target.value)} InputLabelProps={{ shrink: true }}
                inputProps={{ style: { color: TC.primary, fontSize: "0.8rem" } }} sx={INPUT_SX} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Nationality" value={form.nationality || ""}
                onChange={(e) => set("nationality", e.target.value)} InputLabelProps={{ shrink: true }} sx={INPUT_SX} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Occupation" value={form.occupation || ""}
                onChange={(e) => set("occupation", e.target.value)} InputLabelProps={{ shrink: true }} sx={INPUT_SX} />
            </Grid>
          </Grid>
          {err && <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: FS.sm, display: "block", mt: 1 }}>{err}</VuiTypography>}
          <VuiBox mt={1.5} display="flex" gap={1}>
            <VuiButton size="small" color="info" variant="contained" onClick={handleSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : <SaveIcon sx={{ fontSize: "13px !important" }} />}
              sx={{ fontSize: FS.sm }}>
              {saving ? "Saving…" : "Save"}
            </VuiButton>
            <VuiButton size="small" color="secondary" variant="outlined" onClick={() => { setForm({ ...EMPTY_DIRECTOR, ...director }); setOpen(false); setErr(""); }} sx={{ fontSize: FS.sm }}>
              Cancel
            </VuiButton>
          </VuiBox>
        </VuiBox>
      </Collapse>
    </VuiBox>
  );
}

const EMPTY_PSC = { name: "", kind: "individual-person-with-significant-control", naturesOfControl: [], notifiedOn: "", ceasedOn: "", nationality: "", countryOfResidence: "" };

function PscSection({ profile, tenantId, clientId, onProfileUpdated, readonly }) {
  const initPscs = () => (profile.pscs ? JSON.parse(JSON.stringify(profile.pscs)) : []);
  const [pscs,    setPscs]    = useState(initPscs);
  const [dirty,   setDirty]   = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [err,     setErr]     = useState("");
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => { setPscs(initPscs()); setDirty(false); setSaved(false); setOpenIdx(null); }, [profile.id, JSON.stringify(profile.pscs)]);

  const mutate = (updatedPscs) => { setPscs(updatedPscs); setDirty(true); setSaved(false); };

  const setField = (i, k, v) => {
    const updated = pscs.map((p, idx) => idx === i ? { ...p, [k]: v } : p);
    mutate(updated);
  };

  const toggleNature = (i, value) => {
    const current = pscs[i].naturesOfControl || [];
    const updated  = current.includes(value) ? current.filter(n => n !== value) : [...current, value];
    setField(i, "naturesOfControl", updated);
  };

  const addPsc = () => { mutate([...pscs, { ...EMPTY_PSC, id: Date.now().toString() }]); setOpenIdx(pscs.length); };
  const removePsc = (i) => { mutate(pscs.filter((_, idx) => idx !== i)); if (openIdx === i) setOpenIdx(null); };

  const savePscs = async () => {
    setSaving(true); setErr("");
    try {
      await updateClientProfile(tenantId, clientId, { pscs });
      setSaved(true); setDirty(false);
      if (onProfileUpdated) onProfileUpdated();
    } catch (e) { setErr(e?.message || "Save failed."); }
    finally { setSaving(false); }
  };

  const activePscs  = pscs.filter(p => !p.ceasedOn);
  const ceasedPscs  = pscs.filter(p => !!p.ceasedOn);

  return (
    <VuiBox>
      <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1.2}>
        <VuiBox display="flex" alignItems="center" gap={1}>
          <ShieldIcon sx={{ color: TC.link, fontSize: FS.base }} />
          <SectionLabel>🔑 Persons with Significant Control ({activePscs.length} active)</SectionLabel>
        </VuiBox>
        {!readonly && (
          <VuiButton size="small" color="info" variant="outlined" onClick={addPsc}
            startIcon={<AddIcon sx={{ fontSize: "13px !important" }} />} sx={{ fontSize: "0.7rem" }}>
            Add PSC
          </VuiButton>
        )}
        {readonly && <Chip label="🔒 Read-only" size="small" sx={{ height: 18, fontSize: "0.6rem", bgcolor: "rgba(230,81,0,0.08)", color: "#bf360c", border: "1px solid rgba(230,81,0,0.3)" }} />}
      </VuiBox>

      {readonly && <SeparateFilingBanner form="PSC01–PSC09" reason="PSC appointments, cessations, and detail changes must be filed separately." />}

      {pscs.length === 0 ? (
        <VuiBox px={1.5} py={1.5} mb={1} sx={{ ...DARK_CARD_INNER, textAlign: "center" }}>
          <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: FS.sm }}>
            No PSCs recorded. Sync from CH or add manually.
          </VuiTypography>
        </VuiBox>
      ) : (
        pscs.map((psc, i) => (
          <VuiBox key={psc.id || i} mb={1} sx={{
            borderRadius: "10px", overflow: "hidden",
            border: psc.ceasedOn ? "1px solid #e4eef8" : "1px solid #c2d7fb",
            background: psc.ceasedOn ? "#f9fbff" : "#f4f8ff",
          }}>
            <VuiBox display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}>
              <VuiBox display="flex" alignItems="center" gap={1} flex={1}>
                <ShieldIcon sx={{ color: psc.ceasedOn ? TC.tertiary : TC.link, fontSize: "0.9rem" }} />
                <VuiBox>
                  <VuiBox display="flex" alignItems="center" gap={0.7}>
                    <VuiTypography variant="caption" fontWeight="medium" sx={{ color: psc.ceasedOn ? TC.tertiary : TC.primary, fontSize: "0.8rem", textDecoration: psc.ceasedOn ? "line-through" : "none" }}>
                      {psc.name || <em style={{ opacity: 0.5 }}>New PSC</em>}
                    </VuiTypography>
                    {psc.ceasedOn && <Chip label="Ceased" size="small" sx={{ height: 15, fontSize: "0.57rem", bgcolor: "rgba(244,67,54,0.12)", color: TC.error, border: "1px solid rgba(244,67,54,0.3)" }} />}
                  </VuiBox>
                  <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.67rem", opacity: 0.75 }}>
                    {PSC_KINDS.find(k => k.value === psc.kind)?.label || psc.kind || "Individual"}
                    {psc.notifiedOn ? ` · Notified ${fmt(psc.notifiedOn)}` : ""}
                    {psc.naturesOfControl?.length ? ` · ${psc.naturesOfControl.length} control(s)` : ""}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
              <VuiBox display="flex" gap={0.7}>
                {!readonly && <>
                  <VuiButton size="small" color={openIdx === i ? "secondary" : "info"} variant="outlined"
                    onClick={() => setOpenIdx(openIdx === i ? null : i)}
                    startIcon={openIdx === i ? <CloseIcon sx={{ fontSize: "12px !important" }} /> : <EditIcon sx={{ fontSize: "12px !important" }} />}
                    sx={{ fontSize: "0.68rem", minWidth: 70, py: 0.3 }}>
                    {openIdx === i ? "Close" : "Edit"}
                  </VuiButton>
                  <VuiButton size="small" color="secondary" variant="outlined" onClick={() => removePsc(i)}
                    sx={{ minWidth: 30, p: 0.4, fontSize: FS.xs }}>
                    <CloseIcon sx={{ fontSize: FS.md }} />
                  </VuiButton>
                </>}
              </VuiBox>
            </VuiBox>

            <Collapse in={openIdx === i}>
              <Divider sx={{ bordercolor: "#7a94b0" }} />
              <VuiBox p={1.5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Full Name / Company Name *" value={psc.name || ""}
                      onChange={(e) => setField(i, "name", e.target.value)} InputLabelProps={{ shrink: true }} sx={INPUT_SX} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <Select value={psc.kind || "individual-person-with-significant-control"} displayEmpty
                        onChange={(e) => setField(i, "kind", e.target.value)} sx={SELECT_SX}
                        MenuProps={{ PaperProps: { sx: { bgcolor: "rgba(15,18,59,0.97)", color: TC.primary } } }}>
                        {PSC_KINDS.map(k => <MenuItem key={k.value} value={k.value} sx={{ fontSize: FS.md, color: TC.primary }}>{k.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" type="date" label="Notified On" value={psc.notifiedOn || ""}
                      onChange={(e) => setField(i, "notifiedOn", e.target.value)} InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { color: TC.primary, fontSize: "0.8rem" } }} sx={INPUT_SX} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" type="date" label="Ceased On" value={psc.ceasedOn || ""}
                      onChange={(e) => setField(i, "ceasedOn", e.target.value)} InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { color: TC.primary, fontSize: "0.8rem" } }} sx={INPUT_SX} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" label="Nationality" value={psc.nationality || ""}
                      onChange={(e) => setField(i, "nationality", e.target.value)} InputLabelProps={{ shrink: true }} sx={INPUT_SX} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" label="Country of Residence" value={psc.countryOfResidence || ""}
                      onChange={(e) => setField(i, "countryOfResidence", e.target.value)} InputLabelProps={{ shrink: true }} sx={INPUT_SX} />
                  </Grid>
                  <Grid item xs={12}>
                    <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem", display: "block", mb: 0.8 }}>Natures of Control</VuiTypography>
                    <VuiBox display="flex" flexWrap="wrap" gap={0.7}>
                      {NATURES_OF_CONTROL.map(n => {
                        const active = (psc.naturesOfControl || []).includes(n.value);
                        return (
                          <Chip key={n.value} label={n.label} size="small" onClick={() => toggleNature(i, n.value)}
                            sx={{ height: 22, fontSize: "0.67rem", cursor: "pointer", transition: "all 0.15s",
                              bgcolor: active ? "rgba(76,175,80,0.18)" : "rgba(255,255,255,0.07)",
                              color:   active ? "#4caf50"              : "rgba(255,255,255,0.6)",
                              border:  active ? "1px solid rgba(76,175,80,0.5)" : "1px solid rgba(255,255,255,0.15)",
                            }} />
                        );
                      })}
                    </VuiBox>
                  </Grid>
                </Grid>
              </VuiBox>
            </Collapse>
          </VuiBox>
        ))
      )}

      {ceasedPscs.length > 0 && activePscs.length > 0 && (
        <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.67rem", opacity: 0.55, display: "block", mb: 0.5 }}>
          {ceasedPscs.length} ceased PSC{ceasedPscs.length !== 1 ? "s" : ""} shown above
        </VuiTypography>
      )}

      {!readonly && (
        <SaveBar saving={saving} saved={saved} error={err} dirty={dirty}
          onSave={savePscs} onReset={() => { setPscs(initPscs()); setDirty(false); setErr(""); }}
          label="Save PSC Changes" />
      )}
    </VuiBox>
  );
}

function StepOfficersPsc({ profile, directors, tenantId, clientId, onDirectorsUpdated, onProfileUpdated, lastSnapshot }) {
  const activeDirectors   = (directors || []).filter(d => !d.resignedOn);
  const resignedDirectors = (directors || []).filter(d => !!d.resignedOn);
  const lastSnapshotNames = new Set((lastSnapshot?.activeDirectors || []).map(d => d.fullName));
  const hasChanges = lastSnapshot && (
    activeDirectors.some(d => !lastSnapshotNames.has(d.fullName)) ||
    (lastSnapshot.activeDirectors || []).some(d => !new Set(activeDirectors.map(a => a.fullName)).has(d.fullName))
  );

  return (
    <VuiBox>
      {/* Change detection banner */}
      {lastSnapshot && (
        <VuiBox mb={2} p={1.2} sx={{ ...DARK_CARD,
          border: hasChanges ? "1px solid rgba(255,152,0,0.4)" : "1px solid rgba(76,175,80,0.3)",
          background: hasChanges ? "rgba(255,152,0,0.07)" : "rgba(76,175,80,0.07)" }}>
          <VuiBox display="flex" alignItems="center" gap={1}>
            {hasChanges ? <WarningAmberIcon sx={{ color: TC.warning, fontSize: FS.base }} /> : <CheckCircleOutlineIcon sx={{ color: TC.success, fontSize: FS.base }} />}
            <VuiTypography variant="caption" sx={{ color: hasChanges ? "#e65100" : "#2e7d32", fontSize: FS.sm, fontWeight: FW.semibold }}>
              {hasChanges ? `Officer changes detected since last approval (${fmt(lastSnapshot.madeUpTo)})` : `No officer changes since last approval (${fmt(lastSnapshot.madeUpTo)})`}
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      )}

      {/* Directors */}
      <VuiBox mb={2} p={1.5} sx={DARK_CARD}>
        <VuiBox mb={1.2}>
          <SectionLabel>👤 Directors & Officers ({activeDirectors.length} active)</SectionLabel>
        </VuiBox>
        <SeparateFilingBanner form="AP01 / TM01" reason="Director appointments and terminations must be filed separately with Companies House before the CS01." />
        {activeDirectors.length === 0 && resignedDirectors.length === 0 ? (
          <VuiBox px={1.5} py={1.5} sx={{ ...DARK_CARD_INNER, textAlign: "center" }}>
            <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: FS.sm }}>
              No directors recorded. Sync from Companies House or add manually.
            </VuiTypography>
          </VuiBox>
        ) : (
          <>
            {activeDirectors.map((d, i) => (
              <DirectorRow key={d.id || i} director={d} tenantId={tenantId} clientId={clientId}
                onSaved={onDirectorsUpdated} lastSnapshotNames={lastSnapshotNames} readonly={true} />
            ))}
            {resignedDirectors.length > 0 && (
              <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.65rem", display: "block", mt: 1, mb: 0.5 }}>
                {resignedDirectors.length} resigned officer{resignedDirectors.length !== 1 ? "s" : ""}:
              </VuiTypography>
            )}
            {resignedDirectors.map((d, i) => (
              <DirectorRow key={d.id || i} director={d} tenantId={tenantId} clientId={clientId}
                onSaved={onDirectorsUpdated} lastSnapshotNames={lastSnapshotNames} readonly={true} />
            ))}
          </>
        )}
      </VuiBox>

      {/* PSC */}
      <VuiBox p={1.5} sx={DARK_CARD}>
        <PscSection profile={profile} tenantId={tenantId} clientId={clientId}
          onProfileUpdated={onProfileUpdated} readonly={true} />
      </VuiBox>
    </VuiBox>
  );
}

// ─── Step 2: Customer Review ──────────────────────────────────────────────────

function ApprovalSummary({ profile, directors, records }) {
  const activeDirs = (directors || []).filter(d => !d.resignedOn);
  const activePscs = (profile.pscs || []).filter(p => !p.ceasedOn);
  const sc = profile.shareCapital;
  const totalShares = sc?.shareClasses?.reduce((s, c) => s + (parseInt(c.numShares) || 0), 0) || 0;

  return (
    <VuiBox mb={2.5} p={1.5} sx={DARK_CARD}>
      <SectionLabel>📋 Pre-Approval Summary</SectionLabel>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block", mb: 0.3 }}>Company</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.8rem", fontWeight: FW.semibold }}>{profile.legalName || "—"}</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem", display: "block" }}>
            {profile.companyRegNumber} · <span style={{ color: profile.companyStatus === "active" ? "#4caf50" : "#ff9800" }}>{profile.companyStatus}</span>
          </VuiTypography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block", mb: 0.3 }}>Registered Address</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, lineHeight: 1.5 }}>
            {[profile.registeredAddressObj?.addressLine1, profile.registeredAddressObj?.locality, profile.registeredAddressObj?.postalCode].filter(Boolean).join(", ") || profile.registeredAddress || "—"}
          </VuiTypography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>SIC Codes</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.8rem", fontWeight: FW.medium }}>{profile.sicCodes?.length || 0}</VuiTypography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>Active Directors</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: activeDirs.length ? "white" : "#f44336", fontSize: "0.8rem", fontWeight: FW.medium }}>{activeDirs.length}</VuiTypography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>Active PSCs</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: activePscs.length ? "white" : "#ff9800", fontSize: "0.8rem", fontWeight: FW.medium }}>{activePscs.length}</VuiTypography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>Total Shares</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.8rem", fontWeight: FW.medium }}>{totalShares ? totalShares.toLocaleString() : "—"}</VuiTypography>
        </Grid>
        {activeDirs.length > 0 && (
          <Grid item xs={12}>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block", mb: 0.4 }}>Directors</VuiTypography>
            <VuiBox display="flex" flexWrap="wrap" gap={0.6}>
              {activeDirs.map(d => (
                <Chip key={d.id} label={`${d.fullName} (${d.role?.replace(/_/g, " ")})`} size="small"
                  sx={{ height: 20, fontSize: FS.xs, bgcolor: "rgba(0,117,255,0.12)", color: TC.link, border: "1px solid rgba(0,117,255,0.25)" }} />
              ))}
            </VuiBox>
          </Grid>
        )}
        {activePscs.length > 0 && (
          <Grid item xs={12}>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block", mb: 0.4 }}>PSCs</VuiTypography>
            <VuiBox display="flex" flexWrap="wrap" gap={0.6}>
              {activePscs.map((p, i) => (
                <Chip key={i} label={p.name} size="small"
                  sx={{ height: 20, fontSize: FS.xs, bgcolor: "rgba(144,202,249,0.1)", color: TC.link, border: "1px solid rgba(144,202,249,0.2)" }} />
              ))}
            </VuiBox>
          </Grid>
        )}
        {sc?.shareClasses?.length > 0 && (
          <Grid item xs={12}>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block", mb: 0.4 }}>Share Classes</VuiTypography>
            <VuiBox display="flex" flexWrap="wrap" gap={0.6}>
              {sc.shareClasses.map((c, i) => (
                <Chip key={i} label={`${c.name}: ${parseInt(c.numShares || 0).toLocaleString()} shares`} size="small"
                  sx={{ height: 20, fontSize: FS.xs, bgcolor: "rgba(76,175,80,0.1)", color: "#81c784", border: "1px solid rgba(76,175,80,0.25)" }} />
              ))}
            </VuiBox>
          </Grid>
        )}
        {records.length > 0 && (
          <Grid item xs={12}>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs }}>
              Previous approval: <span style={{ color: TC.primary }}>{fmt(records[0].madeUpTo)}</span> by {records[0].approvedByEmail || "—"}
            </VuiTypography>
          </Grid>
        )}
      </Grid>
    </VuiBox>
  );
}

function StepCustomerReview({ profile, tenantId, clientId, madeUpTo, agentNotes, onMadeUpTo, onAgentNotes, review, onSendReview, sending, sendError, onRefreshReview, refreshing }) {
  const status = review?.status || "";
  const statusCfg = {
    sent: { label: "Sent — Awaiting Response", bg: "rgba(21,101,192,0.08)", color: "#1565c0", border: "rgba(21,101,192,0.3)" },
    customer_saved: { label: "Customer Saved Draft", bg: "rgba(230,81,0,0.08)", color: "#e65100", border: "rgba(230,81,0,0.3)" },
    customer_submitted: { label: "Customer Submitted ✓", bg: "rgba(46,125,50,0.08)", color: "#2e7d32", border: "rgba(46,125,50,0.3)" },
  }[status] || { label: "Not Sent", bg: "rgba(0,0,0,0.04)", color: TC.secondary, border: "#d8e6f7" };
  const alreadySent = ["sent", "customer_saved", "customer_submitted"].includes(status);
  const sendDisabled = sending || !profile?.contactEmail;

  return (
    <VuiBox>
      <VuiBox mb={2} p={1.5} sx={DARK_CARD}>
        <SectionLabel>Contact Email</SectionLabel>
        <VuiTypography variant="caption" sx={{ color: profile?.contactEmail ? TC.primary : TC.tertiary, fontSize: FS.md }}>
          {profile?.contactEmail || "No contact email set"}
        </VuiTypography>
      </VuiBox>

      {!profile?.contactEmail && (
        <VuiBox mb={2} px={1.5} py={1.2} sx={{ borderRadius: "8px", background: "rgba(255,152,0,0.12)", border: "1px solid rgba(255,152,0,0.35)" }}>
          <VuiTypography variant="caption" sx={{ color: "#ffb74d", fontSize: "0.78rem" }}>
            No contact email on this profile. Add one in the Contact section before sending.
          </VuiTypography>
        </VuiBox>
      )}

      <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.63rem", letterSpacing: "0.08em", display: "block", mb: 0.6 }}>
        Review Period End Date (Made Up To) *
      </VuiTypography>
      <TextField type="date" value={madeUpTo} onChange={(e) => onMadeUpTo(e.target.value)}
        fullWidth disabled={sending} InputLabelProps={{ shrink: true }}
        inputProps={{ style: { color: TC.primary, fontSize: FS.md } }}
        sx={{ mb: 2, ...INPUT_SX }} />

      <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.63rem", letterSpacing: "0.08em", display: "block", mb: 0.6 }}>
        Agent Notes
      </VuiTypography>
      <TextField multiline minRows={3} maxRows={6} fullWidth disabled={sending}
        placeholder="Add context or instructions for the customer…"
        value={agentNotes} onChange={(e) => onAgentNotes(e.target.value)}
        inputProps={{ style: { color: TC.primary, fontSize: FS.md } }}
        sx={{ mb: 2, ...INPUT_SX }} />

      <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" mb={1.5}>
        <Chip label={statusCfg.label} size="small"
          sx={{ height: 22, fontSize: "0.68rem", fontWeight: FW.bold, bgcolor: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.border}` }} />
        <VuiBox display="flex" gap={1} flexWrap="wrap">
          <VuiButton size="small" color="info" variant="outlined" onClick={onRefreshReview} disabled={refreshing}
            startIcon={refreshing ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : <SyncIcon sx={{ fontSize: "13px !important" }} />}
            sx={{ fontSize: FS.sm, bordercolor: "#4a6080", color: TC.primary, "&:hover": { borderColor: "white", color: TC.primary } }}>
            {refreshing ? "Refreshing…" : "Refresh Status"}
          </VuiButton>
          <VuiButton size="small"
            color={alreadySent ? "warning" : "info"}
            variant="contained"
            onClick={onSendReview}
            disabled={sendDisabled}
            startIcon={sending ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : <SendIcon sx={{ fontSize: "13px !important" }} />}
            sx={{ fontSize: FS.sm }}>
            {sending ? "Sending…" : alreadySent ? "Resend to Customer" : "Send to Customer"}
          </VuiButton>
        </VuiBox>
      </VuiBox>

      {alreadySent && !sending && (
        <VuiTypography variant="caption" sx={{ color: "rgba(255,152,0,0.8)", fontSize: FS.sm, display: "block", mb: 1 }}>
          {status === "customer_submitted"
            ? "⚠ Customer has already submitted. Resending will generate a new secure link and reset the review status."
            : <span style={{ color: TC.success }}>A new secure link will be generated and sent — the previous link will be invalidated.</span>}
        </VuiTypography>
      )}

      {review?.sentToEmail && (
        <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mb: 1.5 }}>
          Sent to {review.sentToEmail}{review.expiresAt ? ` · expires ${fmt(review.expiresAt)}` : ""}
        </VuiTypography>
      )}

      {review?.status === "customer_submitted" && (
        <VuiBox mb={1.5} px={1.5} py={1.2} sx={{ borderRadius: "8px", background: "rgba(76,175,80,0.12)", border: "1px solid rgba(76,175,80,0.35)" }}>
          <VuiTypography variant="caption" sx={{ color: "#2e7d32", fontSize: "0.78rem" }}>
            Customer has submitted their review. Proceed to Agent Approval.
          </VuiTypography>
        </VuiBox>
      )}

      {sendError && (
        <VuiBox mt={1.5} px={1.5} py={1} sx={{ borderRadius: "8px", background: "rgba(244,67,54,0.1)", border: "1px solid rgba(244,67,54,0.3)" }}>
          <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: "0.78rem" }}>{sendError}</VuiTypography>
        </VuiBox>
      )}
    </VuiBox>
  );
}

function StepAgentApproval({ profile, directors, records, review, madeUpTo, notes, declaration, onMadeUpTo, onNotes, onDeclaration, submitting, submitError }) {
  const customerRequestedMadeUpTo = review?.customerChanges?.madeUpTo || "";
  const showRequestedMadeUpTo = customerRequestedMadeUpTo && customerRequestedMadeUpTo !== madeUpTo;
  const rawNotes = review?.customerChanges?.customerNotes || "";

  // Split "Field corrections:\n• Field: text" out of general notes
  const fieldCorrectionBlock = rawNotes.match(/Field corrections:\n([\s\S]*?)(\n\n|$)/);
  const correctionLines = fieldCorrectionBlock
    ? fieldCorrectionBlock[1].split("\n").filter(l => l.startsWith("•")).map(l => {
        const colon = l.indexOf(":");
        return { field: l.slice(1, colon).trim(), note: l.slice(colon + 1).trim() };
      })
    : [];
  const generalNotes = rawNotes.replace(/\n\nField corrections:[\s\S]*/g, "").replace(/Field corrections:[\s\S]*/g, "").trim();
  const hasResponse = rawNotes || showRequestedMadeUpTo;

  // Fields that require a separate CH filing — keyed by field name prefix
  const SEPARATE_FILING = {
    "Registered Address": "AD01",
    "Director":           "AP01 / TM01 / CH01",
    "PSC":                "PSC01–PSC09",
    "SAIL":               "AD02",
  };
  const getSeparateFiling = (fieldName) => {
    for (const [key, form] of Object.entries(SEPARATE_FILING)) {
      if (fieldName === key || fieldName.startsWith(key + "-")) return form;
    }
    return null;
  };

  // Track per-correction decisions: { status: "accepted"|"dismissed"|null, correctedValue: "" }
  const [correctionStatus, setCorrectionStatus] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);

  const setDecision = (field, decision) =>
    setCorrectionStatus(prev => ({
      ...prev,
      [field]: { ...prev[field], status: prev[field]?.status === decision ? null : decision, correctedValue: prev[field]?.correctedValue || "" }
    }));

  const setCorrectedValue = (field, value) =>
    setCorrectionStatus(prev => ({ ...prev, [field]: { ...prev[field], correctedValue: value } }));

  const applyCorrection = (field, correctedValue) => {
    if (!correctedValue.trim()) return;
    const line = `• ${field}: ${correctedValue.trim()}`;
    const current = notes || "";
    const header = "--- Accepted Corrections ---";
    const updated = current.includes(header)
      ? current + "\n" + line
      : (current ? current + "\n\n" : "") + header + "\n" + line;
    onNotes(updated);
    setCorrectionStatus(prev => ({ ...prev, [field]: { ...prev[field], applied: true } }));
  };

  return (
    <VuiBox>
      {/* ── Warning: customer hasn't submitted yet ──────────────────────── */}
      {review?.status !== "customer_submitted" && (
        <VuiBox mb={2} px={1.5} py={1.2} sx={{ borderRadius: "8px", background: "rgba(255,152,0,0.1)", border: "1px solid rgba(255,152,0,0.45)", display: "flex", alignItems: "flex-start", gap: 1 }}>
          <VuiTypography variant="caption" sx={{ fontSize: "1rem", lineHeight: 1 }}>⚠️</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: "#e65100", fontSize: "0.78rem", lineHeight: 1.5 }}>
            <strong>Customer review not yet submitted.</strong>
            {review?.status
              ? ` The review is currently "${review.status.replace(/_/g, " ")}". You can still approve, but any customer corrections will not be included.`
              : " No review has been sent to the customer yet. You can still approve directly."}
          </VuiTypography>
        </VuiBox>
      )}

      {/* ── Customer Response Panel ─────────────────────────────────────── */}
      {hasResponse && (
        <VuiBox mb={2.5} sx={{ ...DARK_CARD, border: "1px solid rgba(33,150,243,0.4)", background: "rgba(13,71,161,0.18)", borderRadius: "12px", overflow: "hidden" }}>
          {/* Header */}
          <VuiBox px={2} py={1.2} sx={{ background: "rgba(33,150,243,0.18)", borderBottom: "1px solid rgba(33,150,243,0.3)", display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon sx={{ color: "#64b5f6", fontSize: FS.base }} />
            <VuiTypography variant="caption" sx={{ color: TC.link, fontWeight: FW.bold, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Customer Response
            </VuiTypography>
            {review?.customerChanges?.submittedAt && (
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, ml: "auto" }}>
                Submitted {fmt(review.customerChanges.submittedAt)}
              </VuiTypography>
            )}
          </VuiBox>

          <VuiBox px={2} py={1.8}>
            {/* Date suggestion */}
            {showRequestedMadeUpTo && (
              <VuiBox mb={1.8} p={1.5} sx={{ borderRadius: "8px", background: "rgba(0,117,255,0.1)", border: "1px solid rgba(0,117,255,0.35)" }}>
                <VuiTypography variant="caption" sx={{ color: TC.link, fontSize: "0.7rem", fontWeight: FW.bold, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", mb: 0.6 }}>
                  📅 Suggested Period End Date
                </VuiTypography>
                <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                  <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.md, fontWeight: FW.semibold }}>
                    {fmt(customerRequestedMadeUpTo)}
                    <span style={{ color: TC.secondary, fontWeight: FW.normal, marginLeft: 8, fontSize: FS.sm }}>
                      (your current: {fmt(madeUpTo) || "not set"})
                    </span>
                  </VuiTypography>
                  <VuiButton size="small" color="info" variant="outlined"
                    onClick={() => onMadeUpTo(customerRequestedMadeUpTo)}
                    sx={{ fontSize: FS.sm, py: 0.4, px: 1.5 }}>
                    ✓ Accept
                  </VuiButton>
                </VuiBox>
              </VuiBox>
            )}

            {/* Field corrections */}
            {correctionLines.length > 0 && (
              <VuiBox mb={1.8}>
                <VuiTypography variant="caption" sx={{ color: "#ffcc80", fontSize: "0.7rem", fontWeight: FW.bold, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", mb: 1 }}>
                  ✏️ Field Corrections Flagged
                </VuiTypography>
                <VuiBox sx={{ borderRadius: "8px", border: "1px solid rgba(255,152,0,0.3)", overflow: "hidden" }}>
                  {correctionLines.map((c, i) => {
                    const cs = correctionStatus[c.field] || {};
                    const status = cs.status;
                    return (
                      <VuiBox key={i} px={1.5} py={1.2}
                        sx={{
                          borderBottom: i < correctionLines.length - 1 ? "1px solid rgba(255,255,255,0.07)" : "none",
                          background: status === "accepted" ? "rgba(76,175,80,0.1)" : status === "dismissed" ? "rgba(255,255,255,0.04)" : "transparent",
                          transition: "background 0.2s",
                        }}>
                        <VuiBox display="flex" alignItems="flex-start" gap={1.5}>
                          <VuiBox flex={1}>
                            <VuiTypography variant="caption" sx={{ color: "#ffcc80", fontSize: FS.sm, fontWeight: FW.bold, display: "block", mb: 0.3 }}>
                              {c.field}
                            </VuiTypography>
                            <VuiTypography variant="caption" sx={{ color: status === "dismissed" ? "rgba(255,255,255,0.35)" : "#fff", fontSize: FS.md, lineHeight: 1.6, textDecoration: status === "dismissed" ? "line-through" : "none" }}>
                              {c.note}
                            </VuiTypography>
                          </VuiBox>
                          <VuiBox display="flex" gap={0.8} flexShrink={0} alignItems="center">
                            <VuiBox component="button" onClick={() => setDecision(c.field, "accepted")}
                              sx={{
                                px: 1.2, py: 0.4, fontSize: "0.7rem", fontWeight: FW.bold, borderRadius: "6px", border: "1px solid",
                                borderColor: status === "accepted" ? "#4caf50" : "rgba(76,175,80,0.4)",
                                background: status === "accepted" ? "rgba(76,175,80,0.25)" : "transparent",
                                color: status === "accepted" ? "#c8e6c9" : "rgba(76,175,80,0.7)",
                                cursor: "pointer", transition: "all 0.15s",
                              }}>
                              ✓ Accept
                            </VuiBox>
                            <VuiBox component="button" onClick={() => setDecision(c.field, "dismissed")}
                              sx={{
                                px: 1.2, py: 0.4, fontSize: "0.7rem", fontWeight: FW.bold, borderRadius: "6px", border: "1px solid",
                                borderColor: status === "dismissed" ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.2)",
                                background: status === "dismissed" ? "rgba(255,255,255,0.12)" : "transparent",
                                color: status === "dismissed" ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.35)",
                                cursor: "pointer", transition: "all 0.15s",
                              }}>
                              ✕ Dismiss
                            </VuiBox>
                          </VuiBox>
                        </VuiBox>

                        {/* Accepted — show corrected value input */}
                        {status === "accepted" && !cs.applied && (
                          <VuiBox mt={1.2} ml={0.5}>
                            <VuiTypography variant="caption" sx={{ color: "#81c784", fontSize: FS.sm, fontWeight: FW.bold, display: "block", mb: 0.6 }}>
                              Enter the corrected value:
                            </VuiTypography>
                            <VuiBox display="flex" gap={1} alignItems="flex-start">
                              <TextField
                                fullWidth size="small"
                                placeholder={`Corrected ${c.field}…`}
                                value={cs.correctedValue || ""}
                                onChange={e => setCorrectedValue(c.field, e.target.value)}
                                multiline minRows={1} maxRows={3}
                                inputProps={{ style: { color: TC.primary, fontSize: FS.md } }}
                                sx={{ ...INPUT_SX, flex: 1 }}
                              />
                              <VuiBox component="button"
                                onClick={() => applyCorrection(c.field, cs.correctedValue || "")}
                                disabled={!cs.correctedValue?.trim()}
                                sx={{
                                  px: 1.5, py: 0.8, fontSize: FS.sm, fontWeight: FW.bold,
                                  borderRadius: "6px", border: "1px solid #4caf50",
                                  background: "rgba(76,175,80,0.2)", color: "#c8e6c9",
                                  cursor: cs.correctedValue?.trim() ? "pointer" : "default",
                                  opacity: cs.correctedValue?.trim() ? 1 : 0.45,
                                  whiteSpace: "nowrap", flexShrink: 0,
                                }}>
                                Apply to Notes
                              </VuiBox>
                            </VuiBox>
                          </VuiBox>
                        )}

                        {cs.applied && (
                          <VuiTypography variant="caption" sx={{ color: "#81c784", fontSize: FS.sm, display: "block", mt: 0.6 }}>
                            ✓ Applied to agent notes below.
                          </VuiTypography>
                        )}
                        {status === "dismissed" && (
                          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.7rem", display: "block", mt: 0.5 }}>
                            Dismissed — data confirmed as correct.
                          </VuiTypography>
                        )}
                      </VuiBox>
                    );
                  })}
                </VuiBox>
                {correctionLines.some(c => correctionStatus[c.field]?.status === "accepted") && (
                  <VuiTypography variant="caption" sx={{ color: "#ffcc80", fontSize: FS.sm, display: "block", mt: 1, lineHeight: 1.6 }}>
                    ⚠️ You have accepted corrections — remember to update the relevant records with Companies House if required.
                  </VuiTypography>
                )}
              </VuiBox>
            )}

            {/* General notes */}
            {generalNotes && (
              <VuiBox>
                <VuiTypography variant="caption" sx={{ color: TC.link, fontSize: "0.7rem", fontWeight: FW.bold, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", mb: 0.8 }}>
                  💬 General Notes
                </VuiTypography>
                <VuiTypography variant="caption" sx={{ color: "#e3f2fd", fontSize: FS.md, lineHeight: 1.7, whiteSpace: "pre-wrap", display: "block" }}>
                  {generalNotes}
                </VuiTypography>
              </VuiBox>
            )}

            {/* No corrections fallback */}
            {!generalNotes && correctionLines.length === 0 && !showRequestedMadeUpTo && (
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.8rem" }}>
                No notes or corrections were submitted by the customer.
              </VuiTypography>
            )}
          </VuiBox>
        </VuiBox>
      )}

      <ApprovalSummary profile={profile} directors={directors} records={records} />

      {/* ── Full Statement Preview ───────────────────────────────────────── */}
      {(() => {
        const getApplied = (field) => {
          const cs = Object.entries(correctionStatus).find(([k]) => k === field)?.[1];
          return cs?.applied ? cs.correctedValue : null;
        };
        const origAddress = [profile.registeredAddressObj?.addressLine1, profile.registeredAddressObj?.locality, profile.registeredAddressObj?.postalCode].filter(Boolean).join(", ") || profile.registeredAddress || "—";
        const activeDirs = (directors || []).filter(d => !d.resignedOn);
        const activePscs = (profile.pscs || []).filter(p => !p.ceasedOn);
        const sc = profile.shareCapital;
        const anyApplied = Object.values(correctionStatus).some(cs => cs.applied);

        const PreviewField = ({ label, original, changed }) => (
          <VuiBox mb={1.4} pb={1.4} sx={{ borderBottom: "1px solid #e4eef8" }}>
            <VuiTypography variant="caption" sx={{ color: TC.link, fontSize: FS.xs, fontWeight: FW.bold, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", mb: 0.4 }}>
              {label}
            </VuiTypography>
            {changed ? (
              <VuiBox display="flex" alignItems="flex-start" gap={1} flexWrap="wrap">
                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.78rem", textDecoration: "line-through", lineHeight: 1.6 }}>
                  {original}
                </VuiTypography>
                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.8rem" }}>→</VuiTypography>
                <VuiTypography variant="caption" sx={{ color: "#c8e6c9", fontSize: FS.md, fontWeight: FW.bold, lineHeight: 1.6, background: "rgba(76,175,80,0.12)", px: 0.8, borderRadius: "4px" }}>
                  {changed}
                </VuiTypography>
              </VuiBox>
            ) : (
              <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.md, lineHeight: 1.6 }}>
                {original || "—"}
              </VuiTypography>
            )}
          </VuiBox>
        );

        return (
          <VuiBox mb={2.5} sx={{ borderRadius: "12px", border: anyApplied ? "1px solid rgba(76,175,80,0.45)" : "1px solid rgba(255,255,255,0.12)", overflow: "hidden" }}>
            {/* Toggle header */}
            <VuiBox
              display="flex" alignItems="center" justifyContent="space-between"
              px={2} py={1.3}
              onClick={() => setPreviewOpen(o => !o)}
              sx={{ cursor: "pointer", background: anyApplied ? "rgba(76,175,80,0.12)" : "rgba(255,255,255,0.05)", "&:hover": { background: anyApplied ? "rgba(76,175,80,0.18)" : "rgba(255,255,255,0.08)" }, transition: "background 0.15s" }}>
              <VuiBox display="flex" alignItems="center" gap={1}>
                <VuiTypography variant="caption" sx={{ fontSize: FS.md, fontWeight: 800, color: anyApplied ? "#81c784" : "#90caf9" }}>
                  👁 Preview Full Statement
                </VuiTypography>
                {anyApplied && (
                  <VuiBox px={1} py={0.2} sx={{ borderRadius: "10px", background: "rgba(76,175,80,0.25)", border: "1px solid rgba(76,175,80,0.5)" }}>
                    <VuiTypography variant="caption" sx={{ color: "#c8e6c9", fontSize: FS.xs, fontWeight: FW.bold }}>
                      {Object.values(correctionStatus).filter(cs => cs.applied).length} correction{Object.values(correctionStatus).filter(cs => cs.applied).length > 1 ? "s" : ""} applied
                    </VuiTypography>
                  </VuiBox>
                )}
                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.7rem" }}>
                  — {previewOpen ? "hide" : "review before approving"}
                </VuiTypography>
              </VuiBox>
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.md }}>
                {previewOpen ? "▲" : "▼"}
              </VuiTypography>
            </VuiBox>

            <Collapse in={previewOpen}>
              <VuiBox px={2.5} pt={2} pb={1.5} sx={{ background: "rgba(0,0,0,0.25)" }}>

                {/* Company identity */}
                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.63rem", fontWeight: FW.bold, textTransform: "uppercase", letterSpacing: "0.1em", display: "block", mb: 1 }}>
                  ── Company Identity
                </VuiTypography>
                <Grid container spacing={2} mb={1}>
                  <Grid item xs={12} sm={6}>
                    <PreviewField label="Registered Name" original={profile.legalName} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PreviewField label="Company Number" original={profile.companyRegNumber} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PreviewField label="Registered Address" original={origAddress} changed={getApplied("Registered Address")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PreviewField label="SIC Codes" original={profile.sicCodes?.join(", ") || "—"} changed={getApplied("SIC Codes")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PreviewField label="Period End Date (Made Up To)"
                      original={fmt(madeUpTo) || "Not set"}
                      changed={showRequestedMadeUpTo && madeUpTo === customerRequestedMadeUpTo ? fmt(customerRequestedMadeUpTo) : getApplied("Period End Date")} />
                  </Grid>
                </Grid>

                {/* Officers */}
                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.63rem", fontWeight: FW.bold, textTransform: "uppercase", letterSpacing: "0.1em", display: "block", mb: 1, mt: 1 }}>
                  ── Officers ({activeDirs.length} active director{activeDirs.length !== 1 ? "s" : ""})
                </VuiTypography>
                {activeDirs.length === 0 ? (
                  <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: "0.78rem", display: "block", mb: 1.5 }}>⚠ No active directors on record</VuiTypography>
                ) : (
                  <VuiBox mb={1.5} sx={{ borderRadius: "8px", border: "1px solid #d8e6f7", overflow: "hidden" }}>
                    {activeDirs.map((d, i) => (
                      <VuiBox key={d.id} px={1.5} py={1} sx={{ borderBottom: i < activeDirs.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none", background: i % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent" }}>
                        <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5}>
                          <VuiTypography variant="caption" sx={{ color: "#e3f2fd", fontSize: "0.8rem", fontWeight: FW.semibold }}>{d.fullName}</VuiTypography>
                          <VuiBox display="flex" gap={0.8} alignItems="center">
                            <Chip label={d.role?.replace(/_/g, " ") || "Director"} size="small"
                              sx={{ height: 18, fontSize: "0.62rem", bgcolor: "rgba(0,117,255,0.15)", color: TC.link, border: "1px solid rgba(0,117,255,0.3)" }} />
                            {d.appointedOn && (
                              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem" }}>
                                apptd {fmt(d.appointedOn)}
                              </VuiTypography>
                            )}
                          </VuiBox>
                        </VuiBox>
                      </VuiBox>
                    ))}
                  </VuiBox>
                )}

                {/* PSCs */}
                {activePscs.length > 0 && (
                  <>
                    <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.63rem", fontWeight: FW.bold, textTransform: "uppercase", letterSpacing: "0.1em", display: "block", mb: 1, mt: 0.5 }}>
                      ── PSC / Persons with Significant Control ({activePscs.length})
                    </VuiTypography>
                    <VuiBox mb={1.5} sx={{ borderRadius: "8px", border: "1px solid #d8e6f7", overflow: "hidden" }}>
                      {activePscs.map((p, i) => (
                        <VuiBox key={i} px={1.5} py={1} sx={{ borderBottom: i < activePscs.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                          <VuiTypography variant="caption" sx={{ color: "#e3f2fd", fontSize: "0.8rem", fontWeight: FW.semibold }}>{p.name}</VuiTypography>
                          {p.naturesOfControl?.length > 0 && (
                            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem", display: "block" }}>
                              {p.naturesOfControl.join(", ")}
                            </VuiTypography>
                          )}
                        </VuiBox>
                      ))}
                    </VuiBox>
                  </>
                )}

                {/* Share capital */}
                {sc?.shareClasses?.length > 0 && (
                  <>
                    <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.63rem", fontWeight: FW.bold, textTransform: "uppercase", letterSpacing: "0.1em", display: "block", mb: 1, mt: 0.5 }}>
                      ── Statement of Capital
                    </VuiTypography>
                    <VuiBox mb={1.5} sx={{ borderRadius: "8px", border: "1px solid #d8e6f7", overflow: "hidden" }}>
                      {sc.shareClasses.map((c, i) => (
                        <VuiBox key={i} px={1.5} py={1} display="flex" justifyContent="space-between" flexWrap="wrap" gap={0.5}
                          sx={{ borderBottom: i < sc.shareClasses.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                          <VuiTypography variant="caption" sx={{ color: "#e3f2fd", fontSize: "0.8rem", fontWeight: FW.semibold }}>{c.name}</VuiTypography>
                          <VuiTypography variant="caption" sx={{ color: TC.midtone, fontSize: "0.78rem" }}>
                            {parseInt(c.numShares || 0).toLocaleString()} shares
                            {c.currency && c.nominalValue ? ` @ ${c.currency} ${c.nominalValue}` : ""}
                          </VuiTypography>
                        </VuiBox>
                      ))}
                    </VuiBox>
                  </>
                )}

                {/* Any other applied corrections not already shown */}
                {correctionLines.filter(c => !["Registered Address","SIC Codes","Period End Date"].includes(c.field) && correctionStatus[c.field]?.applied).length > 0 && (
                  <>
                    <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.63rem", fontWeight: FW.bold, textTransform: "uppercase", letterSpacing: "0.1em", display: "block", mb: 1, mt: 0.5 }}>
                      ── Other Accepted Corrections
                    </VuiTypography>
                    {correctionLines.filter(c => !["Registered Address","SIC Codes","Period End Date"].includes(c.field) && correctionStatus[c.field]?.applied).map((c, i) => (
                      <PreviewField key={i} label={c.field} original={c.note} changed={correctionStatus[c.field]?.correctedValue} />
                    ))}
                  </>
                )}

                <VuiBox mt={1} px={1.5} py={1} sx={{ borderRadius: "8px", background: "rgba(255,152,0,0.08)", border: "1px solid rgba(255,152,0,0.25)" }}>
                  <VuiTypography variant="caption" sx={{ color: "#ffcc80", fontSize: FS.sm, lineHeight: 1.6 }}>
                    ⚠️ This preview reflects data currently held in this system. If corrections were accepted, they are noted above but <strong>Companies House records must be updated separately</strong> via your CH filing credentials.
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
            </Collapse>
          </VuiBox>
        );
      })()}

      <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.63rem", letterSpacing: "0.08em", display: "block", mb: 0.6 }}>
        Review Period End Date (Made Up To) *
      </VuiTypography>
      <TextField type="date" value={madeUpTo} onChange={(e) => onMadeUpTo(e.target.value)}
        fullWidth disabled={submitting} InputLabelProps={{ shrink: true }}
        inputProps={{ style: { color: TC.primary, fontSize: FS.md } }}
        sx={{ mb: 2, ...INPUT_SX }} />

      <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.63rem", letterSpacing: "0.08em", display: "block", mb: 0.6 }}>
        Notes (optional)
      </VuiTypography>
      <TextField multiline minRows={3} maxRows={6} fullWidth disabled={submitting}
        placeholder="Add final approval notes or internal commentary…"
        value={notes} onChange={(e) => onNotes(e.target.value)}
        inputProps={{ style: { color: TC.primary, fontSize: FS.md } }}
        sx={{ mb: 2.5, ...INPUT_SX }} />

      <VuiBox p={2} sx={{ ...DARK_CARD, transition: "all 0.2s", border: declaration ? "1.5px solid rgba(76,175,80,0.6)" : "1.5px solid rgba(0,117,255,0.5)", background: declaration ? "rgba(76,175,80,0.1)" : "rgba(0,117,255,0.1)", boxShadow: declaration ? "0 0 12px rgba(76,175,80,0.15)" : "0 0 10px rgba(0,117,255,0.12)" }}>
        <FormControlLabel
          control={<Checkbox checked={declaration} onChange={(e) => onDeclaration(e.target.checked)} disabled={submitting}
            sx={{ color: TC.secondary, "&.Mui-checked": { color: TC.success }, p: 0.5, mr: 0.5 }} />}
          label={
            <VuiTypography variant="caption" sx={{
              color: declaration ? "#ffffff" : "#e8edf3",
              fontSize: "0.9rem",
              fontWeight: declaration ? 700 : 500,
              lineHeight: 1.7,
              letterSpacing: "0.01em",
            }}>
              I confirm the customer has reviewed and submitted their details, and I am satisfied that this confirmation statement is accurate and authorised to file.
            </VuiTypography>
          }
          sx={{ alignItems: "flex-start", mx: 0 }}
        />
      </VuiBox>

      {submitError && (
        <VuiBox mt={1.5} px={1.5} py={1} sx={{ borderRadius: "8px", background: "rgba(244,67,54,0.1)", border: "1px solid rgba(244,67,54,0.3)" }}>
          <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: "0.78rem" }}>{submitError}</VuiTypography>
        </VuiBox>
      )}
    </VuiBox>
  );
}

// ─── Step 4: Receipt ──────────────────────────────────────────────────────────

function StepReceipt({ record, tenantId, clientId, onFile, filing, filingError, onPoll, polling, pollingError, filedBanner, onStartNew }) {
  if (!record) return null;

  const isFiled         = record.status === "filed";
  const isFilingPending = record.status === "filing_submitted";
  const isFilingFailed  = record.status === "filing_failed";
  const canFile         = !isFiled && !isFilingPending && !isFilingFailed && record.status === "approved";

  // Document action UI state
  const [docBusy, setDocBusy] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  const showToast = (msg, severity = "success") => { setToastMsg(msg); setToastSeverity(severity); setToastOpen(true); };

  const handleCopyPublic = async () => {
    if (!record?.publicDocToken) return showToast("No public link available", "error");
    try {
      const url = getPublicDocumentUrl(record.publicDocToken);
      await navigator.clipboard.writeText(url);
      showToast("Public link copied to clipboard", "success");
    } catch (e) {
      showToast("Failed to copy public link", "error");
    }
  };

  const handleRegenerate = async () => {
    if (!record?.previewDocumentId) return showToast("No generated document to publish.", "error");
    setDocBusy(true);
    try {
      const res = await regenerateDocumentPublicToken(tenantId, clientId, record.previewDocumentId);
      // Mutate local record so UI updates (best-effort)
      if (record && typeof record === 'object') {
        record.publicDocToken = res.token;
        record.publicDocTokenExpiresAt = res.expiresAt;
      }
      try { await navigator.clipboard.writeText(res.publicUrl); showToast("Public link regenerated and copied", "success"); }
      catch { showToast("Public link regenerated (copy failed)", "warning"); }
    } catch (e) {
      console.error("Regenerate token failed:", e);
      showToast(e?.message || "Failed to regenerate public link", "error");
    } finally { setDocBusy(false); }
  };

  const handleRevoke = async () => {
    if (!record?.previewDocumentId) return showToast("No generated document found", "error");
    const ok = window.confirm("Revoke the public link for this document? This cannot be undone.");
    if (!ok) return;
    setDocBusy(true);
    try {
      await revokeDocumentPublicToken(tenantId, clientId, record.previewDocumentId);
      if (record && typeof record === 'object') { record.publicDocToken = null; record.publicDocTokenExpiresAt = null; }
      showToast("Public link revoked", "success");
    } catch (e) {
      console.error("Revoke failed:", e);
      showToast(e?.message || "Failed to revoke public link", "error");
    } finally { setDocBusy(false); }
  };

  /* ── Unified filing status banner ──────────────────────────────────────── */
  const showBanner = filedBanner || !!filingError;
  const bannerIsSuccess = filedBanner && !filingError;
  const bannerStyle = bannerIsSuccess
    ? { background: "#1b5e20", border: "2px solid #2e7d32", color: TC.white }
    : { background: "#b71c1c", border: "2px solid #c62828", color: TC.white };

  return (
    <VuiBox>

      {/* ── Single filing status message box ─────────────────────────────── */}
      {showBanner && (
        <VuiBox mb={2.5} px={2.5} py={2} sx={{
          borderRadius: "12px",
          ...bannerStyle,
          boxShadow: bannerIsSuccess
            ? "0 4px 16px rgba(27,94,32,0.4)"
            : "0 4px 16px rgba(183,28,28,0.4)",
        }}>
          <VuiTypography sx={{ color: TC.white, fontSize: "1rem", fontWeight: 700, display: "block", mb: filingError ? 1 : 0 }}>
            {bannerIsSuccess ? "✅ CS01 Successfully Accepted by Companies House" : "❌ Filing Failed"}
          </VuiTypography>
          {filingError && (
            <VuiTypography sx={{ color: "#ffecec", fontSize: "0.85rem", lineHeight: 1.8, display: "block", whiteSpace: "pre-wrap" }}>
              {filingError}
            </VuiTypography>
          )}
        </VuiBox>
      )}

      <VuiBox mb={3} p={2.5} sx={{ ...DARK_CARD, border: "1px solid rgba(76,175,80,0.45)", background: "rgba(76,175,80,0.09)", textAlign: "center" }}>
        <TaskAltIcon sx={{ color: TC.success, fontSize: "2.5rem", mb: 1 }} />
        <VuiTypography variant="h6" fontWeight="bold" sx={{ color: TC.primary,  fontSize: "1.05rem", mb: 0.5 }}>Confirmation Statement Approved</VuiTypography>
        <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: "0.78rem" }}>This record is locked and stored for audit purposes.</VuiTypography>
      </VuiBox>

      {/* ── Companies House filing section ─────────────────────────────── */}
      <VuiBox mb={2} p={2} sx={{
        ...DARK_CARD,
        border: isFiled
          ? "1px solid rgba(76,175,80,0.5)"
          : isFilingFailed
          ? "1px solid rgba(244,67,54,0.4)"
          : isFilingPending
          ? "1px solid rgba(255,152,0,0.4)"
          : "1px solid rgba(0,117,255,0.35)",
        background: isFiled
          ? "rgba(76,175,80,0.06)"
          : isFilingFailed
          ? "rgba(244,67,54,0.05)"
          : isFilingPending
          ? "rgba(255,152,0,0.05)"
          : "rgba(0,117,255,0.05)",
      }}>
        <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1.2 }}>
          🏛 Companies House Electronic Filing
        </VuiTypography>

        {isFiled ? (
          <VuiBox>
            <VuiBox display="flex" alignItems="center" gap={1} mb={1}>
              <Chip label="FILED WITH COMPANIES HOUSE" size="small"
                sx={{ bgcolor: "rgba(76,175,80,0.15)", color: TC.success, border: "1px solid rgba(76,175,80,0.5)", fontSize: FS.xs, fontWeight: FW.bold, height: 20 }} />
            </VuiBox>
            <Grid container spacing={1}>
              {[
                { label: "Filed At",          value: record.filedAt    ? new Date(record.filedAt).toLocaleString()  : "—" },
                { label: "Filed By",          value: record.filedByEmail || record.filedByUid || "—" },
                { label: "CH Transaction ID", value: record.chTransactionId   || "—" },
                { label: "Filing Reference",  value: record.chFilingReference || "—" },
              ].map(({ label, value }) => (
                <Grid item xs={6} key={label}>
                  <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>{label}</VuiTypography>
                  <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.78rem", fontWeight: FW.medium, wordBreak: "break-all" }}>{value}</VuiTypography>
                </Grid>
              ))}
            </Grid>
          </VuiBox>
        ) : isFilingPending ? (
          <VuiBox>
            <VuiBox display="flex" alignItems="center" gap={1} mb={1}>
              <Chip label="FILING SUBMITTED — PENDING CH ACCEPTANCE" size="small"
                sx={{ bgcolor: "rgba(255,152,0,0.15)", color: TC.warning, border: "1px solid rgba(255,152,0,0.5)", fontSize: FS.xs, fontWeight: FW.bold, height: 20 }} />
            </VuiBox>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mb: 1.2 }}>
              CS01 has been submitted. Companies House is processing it asynchronously — this usually takes a few minutes.
            </VuiTypography>
            <Grid container spacing={1} mb={1.5}>
              {[
                { label: "CH Transaction ID", value: record.chTransactionId   || "—" },
                { label: "CH Status",         value: record.chFilingStatus    || "—" },
                { label: "Last Checked",      value: record.lastPolledAt ? new Date(record.lastPolledAt).toLocaleTimeString() : "—" },
              ].map(({ label, value }) => (
                <Grid item xs={4} key={label}>
                  <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block" }}>{label}</VuiTypography>
                  <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.78rem", fontWeight: FW.medium, wordBreak: "break-all" }}>{value}</VuiTypography>
                </Grid>
              ))}
            </Grid>
            <VuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <VuiButton size="small" color="info" variant="outlined" onClick={onPoll} disabled={polling}
                startIcon={polling ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : <SyncIcon sx={{ fontSize: "14px !important" }} />}
                sx={{ fontSize: FS.sm }}>
                {polling ? "Checking…" : "Refresh Status"}
              </VuiButton>
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs }}>
                Auto-refreshes every 15 s
              </VuiTypography>
            </VuiBox>
            {pollingError && (
              <VuiTypography variant="caption" display="block" sx={{ color: TC.error, fontSize: FS.sm, mt: 0.8 }}>⚠ {pollingError}</VuiTypography>
            )}
          </VuiBox>
        ) : isFilingFailed ? (
          <VuiBox>
            {/* ── Permanently rejected ─────────────────────────────────────── */}
            <VuiBox mb={2} px={2.5} py={2} sx={{ borderRadius: "12px", background: "#b71c1c", border: "2px solid #c62828", boxShadow: "0 4px 16px rgba(183,28,28,0.4)" }}>
              <VuiTypography sx={{ color: TC.white, fontSize: "1rem", fontWeight: 700, display: "block", mb: 0.8 }}>
                ❌ Filing Permanently Rejected by Companies House
              </VuiTypography>
              <VuiTypography sx={{ color: "#ffecec", fontSize: "0.85rem", lineHeight: 1.8, display: "block", whiteSpace: "pre-wrap", mb: 1 }}>
                {record.filingLastError || "Companies House rejected this CS01 filing."}
              </VuiTypography>
              <VuiTypography sx={{ color: "#ffcdd2", fontSize: "0.8rem", display: "block" }}>
                CH rejections are final — the same filing cannot be resubmitted. Correct the issue and start a new CS01.
              </VuiTypography>
            </VuiBox>
            {record.chTransactionId && (
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, display: "block", mb: 1.5, fontFamily: "monospace" }}>
                CH Transaction ID: {record.chTransactionId}
              </VuiTypography>
            )}
            <VuiBox px={1.5} py={1} mb={1.5} sx={{ borderRadius: "8px", background: "#f4f8ff", border: "1px solid #d8e6f7" }}>
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mb: 0.3 }}>Need help?</VuiTypography>
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem" }}>
                Companies House filing enquiries: <strong style={{ color: TC.primary }}>0303 1234 500</strong> (Mon–Fri 8:30am–6pm)
              </VuiTypography>
            </VuiBox>
            <VuiButton size="small" color="info" variant="outlined" onClick={onStartNew}
              sx={{ fontSize: FS.sm }}>
              + Start New CS01
            </VuiButton>
          </VuiBox>
        ) : (
          <VuiBox>
            {/* Previous attempt failed — show stored error prominently */}
            {record.filingLastError && (
              <VuiBox mb={2} px={2.5} py={2} sx={{ borderRadius: "12px", background: "#e65100", border: "2px solid #f57c00", boxShadow: "0 4px 16px rgba(230,81,0,0.35)" }}>
                <VuiTypography sx={{ color: TC.white, fontSize: "0.95rem", fontWeight: 700, display: "block", mb: 0.6 }}>
                  ⚠ Previous Filing Attempt Failed
                </VuiTypography>
                <VuiTypography sx={{ color: "#fff3e0", fontSize: "0.85rem", lineHeight: 1.8, display: "block", whiteSpace: "pre-wrap" }}>
                  {record.filingLastError}
                </VuiTypography>
              </VuiBox>
            )}
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mb: 1.5 }}>
              {record.filingLastError
                ? "Resolve the issue above, then retry below."
                : "The statement has been approved internally. Click below to electronically submit the CS01 to Companies House."}
            </VuiTypography>
            <VuiButton color="info" variant="contained" size="small" onClick={onFile}
              disabled={filing || !canFile}
              startIcon={filing ? <CircularProgress size={13} sx={{ color: "inherit" }} /> : <SendIcon sx={{ fontSize: "14px !important" }} />}
              sx={{ fontSize: FS.sm, minWidth: 220 }}>
              {filing ? "Filing with Companies House…" : record.filingLastError ? "Retry Filing" : "File CS01 with Companies House"}
            </VuiButton>
          </VuiBox>
        )}
      </VuiBox>

      {/* Document preview / links */}
      {(record.previewDocumentId || record.publicDocToken) && (
        <VuiBox mb={2} p={1.5} sx={DARK_CARD}>
          <SectionLabel>📎 Generated Documents</SectionLabel>
          <VuiBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
            {record.previewDocumentId && (
              <VuiButton size="small" variant="outlined" color="info"
                onClick={() => window.open(getDocumentDownloadUrl(tenantId, clientId, record.previewDocumentId), "_blank")}
                startIcon={<LaunchIcon sx={{ fontSize: "14px !important" }} />}>
                View Generated CS
              </VuiButton>
            )}

            {record.publicDocToken ? (
              <>
                <VuiButton size="small" variant="outlined" color="info" onClick={handleCopyPublic} disabled={docBusy}>
                  Copy Public Link
                </VuiButton>
                <VuiButton size="small" variant="contained" color="info"
                  onClick={() => window.open(getPublicDocumentUrl(record.publicDocToken), "_blank")}
                  startIcon={<LaunchIcon sx={{ fontSize: "14px !important" }} />}>
                  Open Public Preview
                </VuiButton>
                <VuiButton size="small" variant="outlined" color="info" onClick={handleRegenerate} disabled={docBusy}>
                  Regenerate Public Link
                </VuiButton>
                <VuiButton size="small" variant="outlined" color="secondary" onClick={handleRevoke} disabled={docBusy}>
                  Revoke Public Link
                </VuiButton>
              </>
            ) : (
              record.previewDocumentId && (
                <VuiButton size="small" variant="outlined" color="info" onClick={handleRegenerate} disabled={docBusy}>
                  Create Public Link
                </VuiButton>
              )
            )}

            {record.publicDocTokenExpiresAt && (
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs }}>
                Expires: {fmt(record.publicDocTokenExpiresAt)}
              </VuiTypography>
            )}
          </VuiBox>

          <Snackbar open={toastOpen} autoHideDuration={5000} onClose={() => setToastOpen(false)}>
            <Alert onClose={() => setToastOpen(false)} severity={toastSeverity} sx={{ width: '100%' }}>
              {toastMsg}
            </Alert>
          </Snackbar>
        </VuiBox>
      )}

      <VuiBox mb={2} p={1.5} sx={DARK_CARD}>
        <SectionLabel>📋 Approval Record</SectionLabel>
        <Grid container spacing={1.5}>
          {[
            { label: "Made Up To", value: fmt(record.madeUpTo) },
            { label: "Approved On", value: fmt(record.approvedAt) },
            { label: "Approved By", value: record.approvedByEmail || record.approvedByUid || "—" },
            { label: "Status", value: record.status?.toUpperCase() },
          ].map(({ label, value }) => (
            <Grid item xs={6} key={label}>
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem", display: "block" }}>{label}</VuiTypography>
              <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.8rem", fontWeight: FW.medium }}>{value}</VuiTypography>
            </Grid>
          ))}
        </Grid>
        {record.notes && <>
          <Divider sx={{ bordercolor: "#7a94b0", my: 1.2 }} />
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem", display: "block", mb: 0.3 }}>Notes</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: "#e0e0e0", fontSize: "0.78rem", lineHeight: 1.5 }}>{record.notes}</VuiTypography>
        </>}
      </VuiBox>
      {record.dataSnapshot && (
        <VuiBox mb={2} p={1.5} sx={DARK_CARD}>
          <SectionLabel>🔒 Data Snapshot at Approval</SectionLabel>
          <Grid container spacing={1.5}>
            {[
              { label: "Company",          value: record.dataSnapshot.companyName },
              { label: "Reg Number",       value: record.dataSnapshot.companyNumber },
              { label: "Status",           value: record.dataSnapshot.companyStatus },
              { label: "Active Directors", value: record.dataSnapshot.activeDirectors?.length ?? 0 },
              { label: "Active PSCs",      value: record.dataSnapshot.activePscs?.length ?? 0 },
              { label: "CH Synced At",     value: fmt(record.dataSnapshot.chLastSyncedAt) },
            ].map(({ label, value }) => (
              <Grid item xs={6} sm={4} key={label}>
                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem", display: "block" }}>{label}</VuiTypography>
                <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.8rem", fontWeight: FW.medium }}>{value ?? "—"}</VuiTypography>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ bordercolor: "#7a94b0", my: 1.2 }} />
          <VuiBox display="flex" alignItems="center" gap={0.8}>
            <LockIcon sx={{ color: TC.secondary, fontSize: FS.md }} />
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs, fontFamily: "monospace" }}>
              SHA-256: {record.snapshotHash?.slice(0, 32)}…
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      )}
      {record.riskFlags?.length > 0 && (
        <VuiBox p={1.5} sx={{ ...DARK_CARD, border: "1px solid rgba(255,152,0,0.25)" }}>
          <SectionLabel>⚠ Risk Flags at Approval</SectionLabel>
          {record.riskFlags.map((f, i) => (
            <VuiTypography key={i} variant="caption" display="block"
              sx={{ color: f.severity === "error" ? "#f44336" : "#ff9800", fontSize: FS.sm, lineHeight: 1.6 }}>
              {f.severity === "error" ? "🚨" : "⚠"} {f.message}
            </VuiTypography>
          ))}
        </VuiBox>
      )}
    </VuiBox>
  );
}

// ─── Audit History ────────────────────────────────────────────────────────────

// ─── CS01 History Panel ───────────────────────────────────────────────────────

function CsHistoryPanel({ records, onGoToReceipt, onPollRecord, pollingRecord, isAdmin, onDelete }) {
  if (!records?.length) return null;
  return (
    <VuiBox mt={3}>
      <VuiTypography variant="caption" fontWeight="bold"
        sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: FS.xs, letterSpacing: "0.08em", display: "block", mb: 1.2 }}>
        📋 All Confirmation Statements ({records.length})
      </VuiTypography>
      <VuiBox display="flex" flexDirection="column" gap={0.8}>
        {records.map(r => {
          const isFiled    = r.status === "filed";
          const isPending  = r.status === "filing_submitted";
          const isFailed   = r.status === "filing_failed";
          const isApproved = r.status === "approved";
          const isPolling  = pollingRecord === r.id;

          const chipCfg = isFiled
            ? { label: "FILED",    bg: "rgba(76,175,80,0.2)",  color: TC.success, border: "rgba(76,175,80,0.5)" }
            : isPending
            ? { label: "PENDING",  bg: "rgba(255,152,0,0.2)",  color: TC.warning, border: "rgba(255,152,0,0.5)" }
            : isFailed
            ? { label: "REJECTED", bg: "rgba(244,67,54,0.2)",  color: TC.error, border: "rgba(244,67,54,0.5)" }
            : { label: "APPROVED", bg: "rgba(0,117,255,0.2)",  color: TC.link, border: "rgba(0,117,255,0.5)" };

          return (
            <VuiBox key={r.id} display="flex" alignItems="center" justifyContent="space-between"
              px={1.5} py={1.2} sx={DARK_CARD_INNER}>
              <VuiBox flex={1} minWidth={0}>
                <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary,  fontSize: "0.78rem" }}>
                  Made up to {fmt(r.madeUpTo)}
                </VuiTypography>
                <VuiTypography variant="caption" display="block"
                  sx={{ color: TC.tertiary,  fontSize: FS.xs, mt: 0.2, opacity: 0.7 }}>
                  Approved {fmt(r.approvedAt)} · {r.approvedByEmail || r.approvedByUid || "—"}
                </VuiTypography>
                {isFiled && (r.chFilingReference || r.chTransactionId) && (
                  <VuiTypography variant="caption"
                    sx={{ color: TC.secondary, fontSize: "0.62rem", fontFamily: "monospace", display: "block", mt: 0.2 }}>
                    CH ref: {r.chFilingReference || r.chTransactionId}
                  </VuiTypography>
                )}
                {isPending && r.chTransactionId && (
                  <VuiTypography variant="caption"
                    sx={{ color: TC.secondary, fontSize: "0.62rem", fontFamily: "monospace", display: "block", mt: 0.2 }}>
                    CH txn: {r.chTransactionId}
                  </VuiTypography>
                )}
                {isFailed && r.filingLastError && (
                  <VuiTypography variant="caption"
                    sx={{ color: TC.error, fontSize: FS.xs, display: "block", mt: 0.2 }}>
                    {r.filingLastError}
                  </VuiTypography>
                )}
              </VuiBox>

              <VuiBox display="flex" alignItems="center" gap={0.8} ml={1.5} flexShrink={0}>
                {r.riskFlags?.length > 0 && (
                  <Tooltip title={`${r.riskFlags.length} risk flag(s) at approval`}>
                    <Chip label={`${r.riskFlags.length}⚑`} size="small"
                      sx={{ height: 17, fontSize: "0.58rem", cursor: "default",
                        bgcolor: "rgba(255,152,0,0.15)", color: TC.warning, border: "1px solid rgba(255,152,0,0.35)" }} />
                  </Tooltip>
                )}
                <Chip label={chipCfg.label} size="small"
                  sx={{ height: 18, fontSize: "0.6rem", fontWeight: FW.bold,
                    bgcolor: chipCfg.bg, color: chipCfg.color,
                    border: `1px solid ${chipCfg.border}` }} />
                {isApproved && (
                  <VuiButton size="small" color="info" onClick={() => onGoToReceipt(r)}
                    startIcon={<SendIcon sx={{ fontSize: "11px !important" }} />}
                    sx={{ fontSize: FS.xs, py: 0.3, px: 1, minWidth: "unset" }}>
                    File Now
                  </VuiButton>
                )}
                {isPending && (
                  <VuiButton size="small" variant="outlined" onClick={() => onPollRecord(r.id)}
                    disabled={isPolling}
                    startIcon={isPolling
                      ? <CircularProgress size={10} sx={{ color: "inherit" }} />
                      : <SyncIcon sx={{ fontSize: "11px !important" }} />}
                    sx={{ fontSize: FS.xs, py: 0.3, px: 1, minWidth: "unset",
                      borderColor: "rgba(255,152,0,0.4)", color: TC.warning,
                      "&:hover": { borderColor: "#ff9800", background: "rgba(255,152,0,0.08)" } }}>
                    {isPolling ? "Checking…" : "Check Status"}
                  </VuiButton>
                )}
                {isAdmin && (
                  <Tooltip title="Delete record (admin only)">
                    <IconButton size="small"
                      onClick={() => onDelete(r.id, r.madeUpTo)}
                      sx={{ color: "#b0bec5", p: 0.3, "&:hover": { color: TC.error } }}>
                      <DeleteOutlineIcon sx={{ fontSize: FS.base }} />
                    </IconButton>
                  </Tooltip>
                )}
              </VuiBox>
            </VuiBox>
          );
        })}
      </VuiBox>
    </VuiBox>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

/*Bearer ${token}` Persist wizard draft state in localStorage keyed by client so each client
 *  retains its own progress independently across page refreshes. */
function useWizardStorage(tenantId, clientId) {
  const key = tenantId && clientId ? `cs-wizard-${tenantId}-${clientId}` : null;

  const read = useCallback(() => {
    if (!key) return {};
    try { return JSON.parse(localStorage.getItem(key) || "{}"); } catch { return {}; }
  }, [key]);

  const write = useCallback((patch) => {
    if (!key) return;
    try {
      const current = JSON.parse(localStorage.getItem(key) || "{}");
      localStorage.setItem(key, JSON.stringify({ ...current, ...patch }));
    } catch {}
  }, [key]);

  const clear = useCallback(() => {
    if (key) { try { localStorage.removeItem(key); } catch {} }
  }, [key]);

  return { read, write, clear };
}

export default function ConfirmationStatementTab({ tenantId, clientId, profile, directors = [], onProfileUpdated, onDirectorsUpdated }) {
  const { isSuperAdmin, isTenantAdmin } = useAuthz();
  const confirm = useConfirm();
  const isAdmin = isSuperAdmin || isTenantAdmin;
  const { read: readStorage, write: writeStorage, clear: clearStorage } = useWizardStorage(tenantId, clientId);

  // Initialise from localStorage if available, otherwise use sensible defaults
  const saved = readStorage();

  const [step, setStepRaw]        = useState(() => Math.min(saved.step ?? 0, STEPS.length - 1));
  const [journeyStage, setJourneyStage] = useState(() => saved.journeyStage || "intro");
  const [tradingOnMarket, setTradingOnMarket] = useState(() => saved.tradingOnMarket || "");
  const [records, setRecords]     = useState([]);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [madeUpTo, setMadeUpToRaw]  = useState(() => saved.madeUpTo || defaultMadeUpTo(profile?.confirmationStatementLastMadeUpTo));
  const [notes, setNotesRaw]        = useState(() => saved.notes || "");
  const [review, setReview]           = useState(null);
  const [sending, setSending]         = useState(false);
  const [sendError, setSendError]     = useState("");
  const [agentNotes, setAgentNotesRaw] = useState(() => saved.agentNotes || "");
  const [refreshing, setRefreshing]   = useState(false);
  const [declaration, setDeclarationRaw] = useState(() => saved.declaration ?? false);
  const [, setAcknowledgedRaw] = useState({});
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [receipt, setReceipt]         = useState(null);
  const [filing, setFiling]           = useState(false);
  const [filingError, setFilingError] = useState("");
  const [polling, setPolling]         = useState(false);
  const [pollingError, setPollingError] = useState("");
  const [pollingRecord, setPollingRecord] = useState(null);
  const [filedBanner, setFiledBanner] = useState(false);
  const pollingInFlight = useRef(false);
  const prevStatusRef   = useRef(null);
  const wizardTopRef    = useRef(null);

  // Persisting setters — update state AND localStorage together
  const setStep         = (v) => { const val = typeof v === "function" ? v(step) : v;         setStepRaw(val);        writeStorage({ step: val }); };
  const setMadeUpTo     = (v) => {                                                             setMadeUpToRaw(v);      writeStorage({ madeUpTo: v }); };
  const setNotes        = (v) => {                                                             setNotesRaw(v);         writeStorage({ notes: v }); };
  const setAgentNotes   = (v) => {                                                             setAgentNotesRaw(v);    writeStorage({ agentNotes: v }); };
  const setDeclaration  = (v) => {                                                             setDeclarationRaw(v);   writeStorage({ declaration: v }); };
  const setJourney = (stage) => { setJourneyStage(stage); writeStorage({ journeyStage: stage }); };
  const setTrading = (value) => { setTradingOnMarket(value); writeStorage({ tradingOnMarket: value }); };

  const load = useCallback(() => {
    if (!tenantId || !clientId) return;
    Promise.all([
      listConfirmationStatements(tenantId, clientId),
      getLatestCsReview(tenantId, clientId).catch(() => ({ review: null })),
    ])
      .then(([{ confirmationStatements }, latestReview]) => {
        setRecords(confirmationStatements);
        setReview(latestReview?.review || null);
        setLoadedOnce(true);
        setLoadError("");
      })
      .catch(err => setLoadError(err?.message || "Failed to load history."));
  }, [tenantId, clientId]);

  useEffect(() => { if (!loadedOnce) load(); }, [load, loadedOnce]);
  useEffect(() => {
    // Only reset madeUpTo from profile if there's no saved draft for this client
    const saved = readStorage();
    if (!saved.madeUpTo) {
      setMadeUpTo(defaultMadeUpTo(profile?.confirmationStatementLastMadeUpTo));
    }
  }, [profile?.confirmationStatementLastMadeUpTo]); // eslint-disable-line

  // Show a success banner when status transitions into "filed"
  useEffect(() => {
    const curr = (receipt || records[0])?.status;
    const prev = prevStatusRef.current;
    if (prev === null) { prevStatusRef.current = curr ?? ""; return; }
    if (curr === "filed" && prev !== "filed") {
      setFiledBanner(true);
      const t = setTimeout(() => setFiledBanner(false), 7000);
      return () => clearTimeout(t);
    }
    prevStatusRef.current = curr ?? "";
  }, [receipt, records]);

  const lastSnapshot     = records[0] || null;
  const canProceed       = [true, true, true, declaration && !!madeUpTo, true];

  const handleSendReview = async () => {
    setSending(true); setSendError("");
    try {
      const result = await sendCsReview(tenantId, clientId, { madeUpTo, agentNotes });
      setReview(result);
    } catch (err) { setSendError(err?.message || "Failed to send."); }
    finally { setSending(false); }
  };

  const handleRefreshReview = async () => {
    setRefreshing(true);
    try {
      const result = await getLatestCsReview(tenantId, clientId);
      setReview(result?.review || null);
    } catch {}
    finally { setRefreshing(false); }
  };

  const handleAgentApprove = async () => {
    if (!review?.id) { setSubmitError("No review found — please send the customer review first, or refresh the page."); return; }
    setSubmitting(true); setSubmitError("");
    try {
      const result = await agentApproveReview(tenantId, clientId, review.id, { notes });
      clearStorage(); // wizard complete — clear saved draft
      setApproveDialogOpen(false);
      setReceipt(result); load(); setStep(4);
    } catch (err) {
      const raw = err?.message || "Approval failed.";
      const m = raw.toLowerCase();
      let friendly = raw;
      if (m.includes("identical data has already been approved") || m.includes("already been approved"))
        friendly = `Duplicate: ${raw}`;
      else if (m.includes("actor uid") || m.includes("not authenticated"))
        friendly = "Authentication error — please refresh and try again.";
      else if (m.includes("not found") && m.includes("review"))
        friendly = "Review record not found — it may have been deleted. Refresh and try again.";
      setSubmitError(friendly);
    }
    finally { setSubmitting(false); }
  };

  const handleFile = async () => {
    const csId = (receipt || records[0])?.id;
    if (!csId) return;
    setFiling(true); setFilingError("");
    try {
      const updated = await fileConfirmationStatement(tenantId, clientId, csId);
      setReceipt((prev) => ({ ...(prev || {}), ...updated }));
      load();
    } catch (err) {
      const raw = err?.message || "";
      const m   = raw.toLowerCase();
      let friendly;

      if (m.includes("no companies house filing") || m.includes("ch_presenter_id") || m.includes("credentials") || m.includes("presenter"))
        friendly = "CH filing credentials are not configured. Ask your system administrator to set CH_PRESENTER_ID and CH_PRESENTER_PASSWORD in the backend .env file, then restart the server.";
      else if (m.includes("ch oauth") || m.includes("oauth failed") || m.includes("401") || m.includes("unauthori"))
        friendly = "Companies House rejected the login credentials. Verify that CH_PRESENTER_ID and CH_PRESENTER_PASSWORD are correct — they may be wrong or expired.";
      else if (m.includes("authentication code") || m.includes("auth code") || m.includes("company_authentication_code"))
        friendly = "The Companies House authentication code for this company is missing or incorrect. Go to the Secrets tab on this client profile and enter the 6-character code sent to the registered office.";
      else if (m.includes("eligibility")) {
        const reason = raw.replace(/ch eligibility check failed:?\s*/i, "").trim();
        if (reason.toLowerCase().includes("no static resource") || reason.toLowerCase().includes("not found")) {
          // CH returned 200 with is_ineligible=true and a "No static resource" reason —
          // this means the company number doesn't exist in the CH filing system.
          const companyMatch = reason.match(/company\/([A-Z0-9]+)\//i);
          const companyNum = companyMatch ? companyMatch[1] : "this company";
          friendly = `Company number ${companyNum} was not found in the Companies House filing system.\n\nPossible causes:\n• The company number on this client profile is incorrect — verify it at https://find-and-update.company-information.service.gov.uk\n• The company is not yet registered for electronic filing (e.g. very recently incorporated)\n• The company has been dissolved or struck off`;
        } else {
          friendly = `Companies House says this company is ineligible to file a CS01 electronically.\n\nReason: ${reason}\n\nCheck the company's status at https://find-and-update.company-information.service.gov.uk and resolve any outstanding issues before retrying.`;
        }
      }
      else if (m.includes("validation failed") || m.includes("ch rejected"))
        friendly = `Companies House rejected the CS01 data. Detail: "${raw.replace(/ch validation failed:?\s*/i, "").trim()}". You cannot resubmit this record — correct the issue and create a new CS01.`;
      else if (m.includes("already been filed") || m.includes("in progress"))
        friendly = "This CS01 has already been submitted to Companies House. Refresh the page to see the latest filing status.";
      else if (m.includes("returned no id") || m.includes("no id"))
        friendly = "Companies House accepted the request but did not return a transaction ID — this is usually a temporary CH API glitch. Wait a minute and try again.";
      else if (m.includes("non-json") || m.includes("unexpected response"))
        friendly = "The Companies House API returned an unexpected response. This is usually a temporary CH outage — check https://developer.company-information.service.gov.uk and try again shortly.";
      else if (m.includes("madeuptodate") || m.includes("made up to") || m.includes("required"))
        friendly = `Missing required filing data: ${raw}. Ensure the "Made Up To" date is set correctly before filing.`;
      else if (m.includes("econnrefused") || m.includes("enotfound") || m.includes("network") || m.includes("fetch"))
        friendly = "Could not reach the Companies House API — check the server's internet connection and try again.";
      else
        friendly = raw || "Filing failed due to an unexpected error. Please try again or contact support if the problem persists.";

      setFilingError(friendly);
      load(); // refresh record so filingLastError banner appears
    } finally {
      setFiling(false);
    }
  };

  const handlePoll = useCallback(async () => {
    const csId = (receipt || records[0])?.id;
    const currentStatus = (receipt || records[0])?.status;
    if (!csId || currentStatus !== "filing_submitted" || pollingInFlight.current) return;
    pollingInFlight.current = true;
    setPolling(true); setPollingError("");
    try {
      const updated = await pollConfirmationStatementStatus(tenantId, clientId, csId);
      setReceipt((prev) => ({ ...(prev || {}), ...updated }));
      load();
    } catch (err) {
      setPollingError(err?.message || "Status check failed. Will retry.");
    } finally {
      setPolling(false);
      pollingInFlight.current = false;
    }
  }, [receipt, records, tenantId, clientId, load]);

  const handleDeleteCs = useCallback(async (csId, madeUpToLabel) => {
    const ok = await confirm({
      title: "Delete CS01 Record",
      message: `Delete CS01 record (made up to ${madeUpToLabel || csId})?\nThis cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await deleteConfirmationStatement(tenantId, clientId, csId);
      const remaining = records.filter((r) => r.id !== csId);
      setRecords(remaining);
      // Always fully reset wizard to step 0 after a delete
      clearStorage();
      setStepRaw(0);
      setJourneyStage("intro"); setTradingOnMarket("");
      setNotesRaw(""); setAgentNotesRaw(""); setDeclarationRaw(false); setAcknowledgedRaw({});
      setSubmitError(""); setSendError(""); setReceipt(null); setReview(null);
      setMadeUpToRaw(defaultMadeUpTo(profile?.confirmationStatementLastMadeUpTo));
      load();
    } catch (err) {
      console.error("Delete CS failed:", err);
    }
  }, [tenantId, clientId, receipt, records, load, profile, clearStorage]);

  // Auto-poll every 15 s while on receipt step and status is filing_submitted
  useEffect(() => {
    const current = receipt || records[0];
    if (step !== 4 || current?.status !== "filing_submitted") return;
    const intervalId = setInterval(handlePoll, 15000);
    return () => clearInterval(intervalId);
  }, [step, receipt, records, handlePoll]);

  const handleNext = () => { if (step === 3) { handleAgentApprove(); return; } if (step < 5) setStep(s => s + 1); };
  const handleBack = () => { if (step > 0) setStep(s => s - 1); };
  const handleGoToStep = (n) => { if (n >= 0 && n <= step) setStep(n); };

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  const handleStartNew = () => {
    clearStorage();
    setStepRaw(0); setNotesRaw(""); setAgentNotesRaw(""); setDeclarationRaw(false); setAcknowledgedRaw({});
    setJourneyStage("intro"); setTradingOnMarket("");
    setSubmitError(""); setSendError(""); setReceipt(null); setReview(null);
    setMadeUpToRaw(defaultMadeUpTo(profile?.confirmationStatementLastMadeUpTo));
    // Clear any pending customer review records in Firestore (fire-and-forget)
    clearPendingCsReviews(tenantId, clientId).catch(() => {});
  };

  // Load a historical record into the receipt step for filing
  const handleGoToReceipt = (record) => {
    setReceipt(record);
    setFilingError("");
    setStep(4);
    // Scroll the wizard into view so the user sees the filing UI
    setTimeout(() => wizardTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  // Poll a specific record from the history panel (independent of main receipt polling)
  const handlePollRecord = useCallback(async (csId) => {
    if (pollingRecord === csId) return;
    setPollingRecord(csId);
    try {
      const updated = await pollConfirmationStatementStatus(tenantId, clientId, csId);
      setRecords(prev => prev.map(r => r.id === csId ? { ...r, ...updated } : r));
      if (receipt?.id === csId) setReceipt(prev => ({ ...prev, ...updated }));
    } catch {
      // non-critical — record stays unchanged
    } finally {
      setPollingRecord(null);
    }
  }, [pollingRecord, tenantId, clientId, receipt]);

  const isLastStep = step === STEPS.length - 1;

  const [guideOpen, setGuideOpen] = useState(false); // kept for compat
  const [previewOpen, setPreviewOpen] = useState(false);

  const sectionStatus = useMemo(() => {
    const reviewStatus = review?.status;
    const activeCount = (directors || []).filter(d => !d.resignedOn).length;
    const activePscCount = (profile?.pscs || []).filter(p => !p.ceasedOn).length;
    const latestRecord = receipt || records[0];
    return [
      // 0: Company Data
      { dot: "#4caf50", sublabel: "SIC codes & share capital" },
      // 1: Officers & PSC
      { dot: "#4caf50", sublabel: `${activeCount} director${activeCount !== 1 ? "s" : ""} · ${activePscCount} PSC${activePscCount !== 1 ? "s" : ""}` },
      // 2: Customer Review
      reviewStatus === "customer_submitted" ? { dot: "#4caf50",              sublabel: "Submitted ✓" }
        : reviewStatus === "customer_saved" ? { dot: "#64b5f6",              sublabel: "Draft saved" }
        : reviewStatus === "sent"           ? { dot: "#64b5f6",              sublabel: "Awaiting response" }
        :                                     { dot: "rgba(255,255,255,0.18)", sublabel: "Not sent yet" },
      // 3: Agent Approval
      declaration && madeUpTo              ? { dot: "#4caf50",              sublabel: "Ready to approve" }
        : reviewStatus === "customer_submitted" ? { dot: "#ff9800",         sublabel: "Action required" }
        :                                         { dot: "rgba(255,255,255,0.18)", sublabel: "Awaiting customer" },
      // 4: Receipt
      latestRecord?.status === "accepted" || latestRecord?.status === "filed"
        ? { dot: "#4caf50",  sublabel: "Filed ✓" }
        : latestRecord?.status === "filing_submitted"
          ? { dot: "#64b5f6", sublabel: "Filing in progress" }
          : { dot: "rgba(255,255,255,0.18)", sublabel: "" },
    ];
  }, [review, directors, profile, declaration, madeUpTo, receipt, records]);

  if (journeyStage === "intro") {
    return (
      <VuiBox sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "230px minmax(0, 1fr)" }, gap: { xs: 2, md: 3 }, alignItems: "start" }}>
        <StepperBar step={0} sectionStatus={sectionStatus} onSelect={() => {}} />
        <JourneyIntro profile={profile} madeUpTo={madeUpTo} onStart={() => setJourney("trading")} />
      </VuiBox>
    );
  }

  if (journeyStage === "trading") {
    return (
      <VuiBox sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "230px minmax(0, 1fr)" }, gap: { xs: 2, md: 3 }, alignItems: "start" }}>
        <StepperBar step={0} sectionStatus={sectionStatus} onSelect={() => {}} />
        <TradingQuestion
          profile={profile}
          madeUpTo={madeUpTo}
          value={tradingOnMarket}
          onChange={setTrading}
          onBack={() => setJourney("intro")}
          onContinue={() => setJourney("workflow")}
        />
      </VuiBox>
    );
  }

  return (
    <VuiBox sx={{ background: TC.white, color: TC.primary, p: { xs: 1.5, sm: 3 }, borderRadius: "12px" }}>
      {/* ── CS01 header bar ──────────────────────────────────────────────── */}
      {(() => {
        const nextDue    = profile?.confirmationStatementNextDue;
        const days       = daysUntil(nextDue);
        const dueBg     = days === null ? "transparent" : days < 0 ? "rgba(211,47,47,0.13)" : days <= 14 ? "rgba(255,109,0,0.13)" : days <= 30 ? "rgba(255,152,0,0.12)" : "rgba(56,142,60,0.12)";
        const dueBorder = days === null ? "transparent" : days < 0 ? "rgba(211,47,47,0.5)" : days <= 14 ? "rgba(255,109,0,0.5)" : days <= 30 ? "rgba(255,152,0,0.45)" : "rgba(56,142,60,0.4)";
        const dueColor  = days === null ? TC.secondary : days < 0 ? "#c62828" : days <= 14 ? "#e65100" : days <= 30 ? "#ef6c00" : "#2e7d32";
        const dueLabel  = days === null ? "—" : days < 0 ? `Overdue by ${Math.abs(days)}d` : days === 0 ? "Due today" : days === 1 ? "Due tomorrow" : `Due in ${days}d`;

        return (
          <VuiBox mb={2.5} px={2} py={1.5} sx={{
            borderRadius: "12px",
            background: days === null ? "rgba(0,117,255,0.07)" : dueBg,
            border: `1px solid ${days === null ? "rgba(0,117,255,0.2)" : dueBorder}`,
            transition: "background 0.3s, border-color 0.3s",
          }}>
            {/* Row 1: title + actions */}
            <VuiBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1.2}>
              <VuiBox display="flex" alignItems="center" gap={1}>
                <AssignmentTurnedInIcon sx={{ color: days === null ? TC.linkHover : dueColor, fontSize: "1.05rem" }} />
                <VuiTypography sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: "0.95rem", letterSpacing: "-0.01em" }}>
                  Confirmation Statement — CS01
                </VuiTypography>
                <VuiTypography sx={{ color: TC.midtone, fontSize: "0.85rem", fontWeight: FW.semibold }}>
                  {profile?.legalName || "—"}
                </VuiTypography>
              </VuiBox>
              <VuiBox display="flex" alignItems="center" gap={1}>
                {isLastStep && (
                  <VuiButton color="info" variant="outlined" size="small" onClick={handleStartNew} sx={{ fontSize: FS.sm }}>
                    + New CS01
                  </VuiButton>
                )}
                <VuiButton color="info" variant="contained" size="small" onClick={() => setPreviewOpen(true)}
                  startIcon={<VisibilityIcon sx={{ fontSize: "14px !important" }} />}
                  sx={{ fontSize: FS.sm, background: "#0057d8", color: TC.white, border: "1px solid #0057d8", "&:hover": { background: "#0041a8", borderColor: "#0041a8" } }}>
                  Preview CS01
                </VuiButton>
              </VuiBox>
            </VuiBox>

            {/* Row 2: summary chips */}
            <VuiBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              {/* Due date badge */}
              <VuiBox px={1.2} py={0.4} sx={{ borderRadius: "20px", background: dueBg, border: `1px solid ${dueBorder}`, display: "flex", alignItems: "center", gap: 0.6 }}>
                <VuiTypography variant="caption" sx={{ fontSize: "0.66rem", color: TC.tertiary, fontWeight: FW.medium }}>Next Due:</VuiTypography>
                <VuiTypography variant="caption" sx={{ fontSize: "0.72rem", color: TC.primary, fontWeight: FW.semibold }}>{fmt(nextDue)}</VuiTypography>
                {nextDue && (
                  <VuiTypography variant="caption" sx={{ fontSize: "0.65rem", color: dueColor, fontWeight: FW.bold }}>· {dueLabel}</VuiTypography>
                )}
              </VuiBox>
            </VuiBox>
          </VuiBox>
        );
      })()}

      {/* ── Progress pane + content ─────────────────────────────────────── */}
      <div ref={wizardTopRef} />
      <VuiBox sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "230px minmax(0, 1fr)" }, gap: { xs: 2, md: 3 }, alignItems: "start" }}>
        <StepperBar step={step} sectionStatus={sectionStatus} onSelect={setStep} />

        {/* Content area */}
        <VuiBox minWidth={0}>
        {/* Section content */}
        <VuiBox mb={3}>
            {step === 0 && (
              <>
                <VuiTypography component="h2" sx={{ color: TC.primary, fontSize: FS.lg, fontWeight: FW.bold, mb: 1 }}>
                  Check company information
                </VuiTypography>
                <VuiTypography sx={{ color: TC.midtone, fontSize: FS.base, lineHeight: 1.5, mb: 2 }}>
                  Use the LedgerHub controls below to review and update the information needed for this filing.
                </VuiTypography>
                <StepCompanyData profile={profile} tenantId={tenantId} clientId={clientId}
                  onProfileUpdated={onProfileUpdated} onDirectorsUpdated={onDirectorsUpdated} />
              </>
            )}
            {step === 1 && (
              <StepOfficersPsc profile={profile} directors={directors} tenantId={tenantId} clientId={clientId}
                onDirectorsUpdated={onDirectorsUpdated} onProfileUpdated={onProfileUpdated}
                lastSnapshot={lastSnapshot?.dataSnapshot || null} />
            )}
            {step === 2 && (
              <StepCustomerReview profile={profile} tenantId={tenantId} clientId={clientId}
                madeUpTo={madeUpTo} agentNotes={agentNotes} onMadeUpTo={setMadeUpTo} onAgentNotes={setAgentNotes}
                review={review} onSendReview={handleSendReview} sending={sending} sendError={sendError}
                onRefreshReview={handleRefreshReview} refreshing={refreshing} />
            )}
            {step === 3 && (
              <StepAgentApproval profile={profile} directors={directors} records={records} review={review}
                madeUpTo={madeUpTo} notes={notes} declaration={declaration}
                onMadeUpTo={setMadeUpTo} onNotes={setNotes} onDeclaration={setDeclaration}
                submitting={submitting} submitError={submitError} />
            )}
            {step === 4 && (
              <StepReceipt tenantId={tenantId} clientId={clientId} record={receipt || records[0]} onFile={handleFile} filing={filing}
                filingError={filingError} onPoll={handlePoll} polling={polling}
                pollingError={pollingError} filedBanner={filedBanner} onStartNew={handleStartNew} />
            )}
          </VuiBox>

          {/* ── Bottom action bar ─────────────────────────────────────────── */}
          <VuiBox pt={2} sx={{ borderTop: "1px solid rgba(148,163,184,0.25)" }}
            display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <VuiBox minWidth={{ xs: "100%", sm: 180 }}>
              {step > 0 && (
                <VuiButton color="info" variant="outlined" size="small"
                  onClick={() => setStep(s => Math.max(s - 1, 0))}
                  startIcon={<ArrowBackIcon sx={{ fontSize: "13px !important" }} />}
                  sx={{ fontSize: FS.sm, px: 2 }}>
                  Previous: {STEPS[step - 1]?.label}
                </VuiButton>
              )}
            </VuiBox>

            <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm, textAlign: "center", display: { xs: "none", sm: "block" } }}>
              {STEPS[step]?.label}
            </VuiTypography>

            <VuiBox minWidth={{ xs: "100%", sm: 180 }} display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
              {step === 3 ? (
                <VuiButton color="success" variant="contained" onClick={() => { setSubmitError(""); setApproveDialogOpen(true); }}
                  disabled={!canProceed[3] || submitting}
                  endIcon={<CheckCircleOutlineIcon sx={{ fontSize: "13px !important" }} />}
                  sx={{ fontSize: FS.sm, px: 2.5 }}>
                  Approve &amp; confirm
                </VuiButton>
              ) : step < STEPS.length - 1 ? (
                <VuiButton color="info" variant="contained" size="small"
                  onClick={() => setStep(s => Math.min(s + 1, STEPS.length - 1))}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: "13px !important" }} />}
                  sx={{ fontSize: FS.sm, px: 2.5 }}>
                  Next: {STEPS[step + 1]?.label}
                </VuiButton>
              ) : null}
            </VuiBox>
          </VuiBox>
        </VuiBox>
      </VuiBox>

      {loadError && (
        <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: FS.sm, display: "block", mt: 1 }}>{loadError}</VuiTypography>
      )}

      {loadedOnce && records.length > 0 && (
        <>
          <Divider sx={{ bordercolor: "#7a94b0", my: 3 }} />
          <CsHistoryPanel
            records={records}
            onGoToReceipt={handleGoToReceipt}
            onPollRecord={handlePollRecord}
            pollingRecord={pollingRecord}
            isAdmin={isAdmin}
            onDelete={handleDeleteCs}
          />
        </>
      )}

      {/* Preview Modal */}
      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        profile={profile}
        directors={directors}
        madeUpTo={madeUpTo}
      />

      {/* ── Agent Approval Confirmation Dialog ────────────────────────── */}
      <Dialog open={approveDialogOpen} onClose={() => !submitting && setApproveDialogOpen(false)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", background: "linear-gradient(145deg,#0d1b3e,#071028)", border: "1px solid rgba(0,117,255,0.25)", boxShadow: "0 24px 60px rgba(0,0,0,0.6)" } }}>

        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3, display: "flex", alignItems: "center", gap: 1.5, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <ShieldIcon sx={{ color: TC.success, fontSize: "1.4rem" }} />
          <VuiTypography sx={{ fontWeight: FW.bold, fontSize: FS.base, color: "#fff", flex: 1 }}>
            Review &amp; Approve Confirmation Statement
          </VuiTypography>
          <IconButton size="small" onClick={() => setApproveDialogOpen(false)} disabled={submitting}
            sx={{ color: "rgba(255,255,255,0.4)", "&:hover": { color: "#fff" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          {/* Company */}
          <VuiBox mb={2} p={2} sx={{ borderRadius: "10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <VuiTypography sx={{ fontSize: "0.68rem", fontWeight: FW.bold, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", mb: 1 }}>
              Company
            </VuiTypography>
            <VuiTypography sx={{ fontSize: "0.92rem", fontWeight: FW.bold, color: "#fff", mb: 0.3 }}>
              {profile?.legalName || "—"}
            </VuiTypography>
            {profile?.companyRegNumber && (
              <VuiTypography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)" }}>
                Company No. {profile.companyRegNumber}
              </VuiTypography>
            )}
          </VuiBox>

          {/* Made Up To */}
          <VuiBox mb={2} p={2} sx={{ borderRadius: "10px", background: "rgba(0,117,255,0.07)", border: "1px solid rgba(0,117,255,0.25)" }}>
            <VuiTypography sx={{ fontSize: "0.68rem", fontWeight: FW.bold, color: "rgba(100,181,246,0.7)", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.8 }}>
              Statement Made Up To
            </VuiTypography>
            <VuiTypography sx={{ fontSize: FS.base, fontWeight: FW.bold, color: "#64b5f6" }}>
              {madeUpTo ? fmt(madeUpTo) : "—"}
            </VuiTypography>
          </VuiBox>

          {/* Active Directors */}
          <VuiBox mb={2} p={2} sx={{ borderRadius: "10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <VuiTypography sx={{ fontSize: "0.68rem", fontWeight: FW.bold, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", mb: 1 }}>
              Active Directors ({directors.filter(d => !d.resignedOn).length})
            </VuiTypography>
            {directors.filter(d => !d.resignedOn).length === 0
              ? <VuiTypography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.35)" }}>No active directors</VuiTypography>
              : directors.filter(d => !d.resignedOn).map((d, i) => (
                <VuiBox key={i} display="flex" alignItems="center" gap={1} mb={0.5}>
                  <PersonIcon sx={{ fontSize: FS.md, color: "rgba(255,255,255,0.4)" }} />
                  <VuiTypography sx={{ fontSize: "0.83rem", color: "rgba(255,255,255,0.85)" }}>{d.fullName}</VuiTypography>
                  <VuiTypography sx={{ fontSize: FS.sm, color: "rgba(255,255,255,0.4)", ml: "auto" }}>{d.role || "Director"}</VuiTypography>
                </VuiBox>
              ))
            }
          </VuiBox>

          {/* Agent Notes */}
          {notes?.trim() && (
            <VuiBox mb={2} p={2} sx={{ borderRadius: "10px", background: "rgba(255,152,0,0.07)", border: "1px solid rgba(255,152,0,0.25)" }}>
              <VuiTypography sx={{ fontSize: "0.68rem", fontWeight: FW.bold, color: "rgba(255,204,128,0.8)", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.8 }}>
                Agent Notes
              </VuiTypography>
              <VuiTypography sx={{ fontSize: FS.md, color: "rgba(255,255,255,0.75)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                {notes}
              </VuiTypography>
            </VuiBox>
          )}

          {/* Confirmation warning */}
          <VuiBox p={2} mb={1} sx={{ borderRadius: "10px", background: "rgba(76,175,80,0.08)", border: "1px solid rgba(76,175,80,0.3)", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <CheckCircleIcon sx={{ color: TC.success, fontSize: "1.1rem", mt: 0.1, flexShrink: 0 }} />
            <VuiTypography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>
              By approving, you confirm that all company information is accurate and ready for filing with Companies House. This action cannot be undone.
            </VuiTypography>
          </VuiBox>

          {submitError && (
            <VuiBox mt={1.5} px={1.5} py={1.2} sx={{ borderRadius: "8px", background: "rgba(244,67,54,0.12)", border: "1px solid rgba(244,67,54,0.4)", display: "flex", alignItems: "flex-start", gap: 1 }}>
              <VuiTypography sx={{ fontSize: "1rem", lineHeight: 1, flexShrink: 0 }}>❌</VuiTypography>
              <VuiTypography sx={{ fontSize: "0.8rem", color: "#ff6b6b", lineHeight: 1.5 }}>{submitError}</VuiTypography>
            </VuiBox>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <VuiButton variant="outlined" onClick={() => setApproveDialogOpen(false)} disabled={submitting}
            sx={{ fontSize: FS.md, px: 2.5, borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.6)", "&:hover": { borderColor: "rgba(255,255,255,0.4)", background: "rgba(255,255,255,0.05)" } }}>
            Cancel
          </VuiButton>
          <VuiButton variant="contained" onClick={handleAgentApprove} disabled={submitting}
            startIcon={submitting ? <CircularProgress size={13} sx={{ color: "inherit" }} /> : <CheckCircleOutlineIcon sx={{ fontSize: "13px !important" }} />}
            sx={{ fontSize: FS.md, px: 3, background: "linear-gradient(135deg,#2e7d32,#43a047)", "&:hover": { background: "linear-gradient(135deg,#1b5e20,#2e7d32)" } }}>
            {submitting ? "Approving…" : "Approve & Submit"}
          </VuiButton>
        </DialogActions>
      </Dialog>
    </VuiBox>
  );
}
